import { randomUUID } from "node:crypto";
import { resolve } from "node:path";
import { stat } from "node:fs/promises";
import { ensureClaudeProfile } from "../claude-profile.js";
import type { ClaudeTokenStore } from "../claude-token-store.js";
import { resolveCanonicalProjectId } from "../git-remote-resolver.js";
import {
  AGENT_LIFECYCLE_STATUSES,
  type AgentLifecycleStatus,
} from "../../shared/agent-lifecycle.js";
import type { Logger } from "pino";
import { z } from "zod";
import type { TerminalManager } from "../../terminal/terminal-manager.js";
import type { AuthServerSessionClient } from "../auth-server-client.js";
import type { WorkspaceRegistry } from "../workspace-registry.js";

import type {
  AgentCapabilityFlags,
  AgentClient,
  AgentFeature,
  AgentLaunchContext,
  AgentSlashCommand,
  AgentMode,
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentPermissionResult,
  AgentPersistenceHandle,
  AgentPromptInput,
  AgentProvider,
  AgentRunOptions,
  AgentRunResult,
  AgentSession,
  AgentSessionConfig,
  AgentStreamEvent,
  AgentTimelineItem,
  McpServerConfig,
  AgentUsage,
  AgentRuntimeInfo,
  ListPersistedAgentsOptions,
  PersistedAgentDescriptor,
} from "./agent-sdk-types.js";
import { getSessionMessages } from "@anthropic-ai/claude-agent-sdk";
import { findClaudeSessionFile, forkSession } from "./session-fork.js";
import { isClaudeTranscriptNoiseContent } from "./providers/claude-agent.js";
import { enumerateClaudeDraftCommands } from "./providers/claude-draft-commands.js";
import { dirname, join } from "node:path";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Claude's transcript JSONL encodes tool_result payloads as messages with
// type:"user" (the same role the tool result is delivered back to the model
// under). They must be filtered out anywhere we count "real user turns" —
// otherwise positional indices drift wildly vs the rendered timeline.
export function isToolResultUserMessage(message: unknown): boolean {
  if (typeof message !== "object" || message === null) return false;
  const content = (message as { content?: unknown }).content;
  if (!Array.isArray(content)) return false;
  return content.some(
    (c) => typeof c === "object" && c !== null && (c as { type?: unknown }).type === "tool_result",
  );
}
import type { StoredAgentRecord, AgentStorage } from "./agent-storage.js";
import {
  InMemoryAgentTimelineStore,
  type SeedAgentTimelineOptions,
} from "./agent-timeline-store.js";
import type {
  AgentTimelineFetchOptions,
  AgentTimelineFetchResult,
  AgentTimelineRow,
  AgentTimelineStore,
} from "./agent-timeline-store-types.js";
import {
  AGENT_STREAM_COALESCE_DEFAULT_WINDOW_MS,
  AgentStreamCoalescer,
} from "./agent-stream-coalescer.js";
import { getAgentProviderDefinition } from "./provider-manifest.js";
import { appendAppostleRuntimeContext } from "./appostle-system-prompt.js";

export { AGENT_LIFECYCLE_STATUSES, type AgentLifecycleStatus };

/**
 * Thrown when a guest (a connecting user who is not the daemon owner) tries to
 * spawn a Claude Code agent without having added their own Claude account on
 * this machine. Surfaced to the client so they can paste their token, instead
 * of silently falling back to the owner's Claude subscription.
 */
export class ClaudeAccountRequiredError extends Error {
  constructor(public readonly userId: string) {
    super(
      "Add your Claude account on this machine to run Claude Code agents here. " +
        "Run `claude setup-token` on your own computer and paste the token in Settings.",
    );
    this.name = "ClaudeAccountRequiredError";
  }
}

/**
 * Internal-only placeholder for the `ownerUserId` field on agents created
 * via test paths that bypass the websocket-server gate (which is the only
 * production path into createAgent and always carries a real auth-server
 * userId). Exposed for tests that need to assert against the value.
 *
 * Production code never sees this string — websocket-server rejects
 * unresolvable peers with WS close code 1008 before they can spawn an
 * agent. Bootstrap also refuses to start an unlinked daemon. The placeholder
 * exists only so direct AgentManager unit tests (which construct the
 * manager in isolation) keep compiling without threading a userId through
 * every call site.
 */
export const AGENT_MANAGER_TEST_OWNER_PLACEHOLDER = "__test_owner__";
export type {
  AgentTimelineCursor,
  AgentTimelineFetchDirection,
  AgentTimelineFetchOptions,
  AgentTimelineFetchResult,
  AgentTimelineRow,
  AgentTimelineWindow,
} from "./agent-timeline-store-types.js";

export type AgentManagerEvent =
  | { type: "agent_state"; agent: ManagedAgent }
  | {
      type: "agent_stream";
      agentId: string;
      event: AgentStreamEvent;
      seq?: number;
      epoch?: string;
    };

export type AgentSubscriber = (event: AgentManagerEvent) => void;

export type SubscribeOptions = {
  agentId?: string;
  replayState?: boolean;
};

export type PersistedAgentQueryOptions = ListPersistedAgentsOptions & {
  provider?: AgentProvider;
};

export type AgentAttentionCallback = (params: {
  agentId: string;
  provider: AgentProvider;
  reason: "finished" | "error" | "permission";
}) => void;

export type ProviderAvailability = {
  provider: AgentProvider;
  available: boolean;
  error: string | null;
};

/**
 * Callback that resolves an agent's cwd to a workspaceId and adds (or
 * extends) a todo. The MCP layer hands us structured fields synthesized by
 * the LLM; if it punts it can pass raw markdown as a fallback that the
 * daemon parses best-effort.
 *
 * Smart-merge: when `mergeIntoTodoId` is provided, the new subtasks (or the
 * title if no subtasks) are appended as subtasks under the existing parent
 * todo. The parent's title + notes are never touched.
 */
export type AddTodoFromAgent = (input: {
  cwd: string;
  agentId: string;
  /** Short headline, ≤200 chars. Required unless `markdown` is provided. */
  title?: string;
  /** Markdown body for long-form context. No length cap. */
  notes?: string;
  /** Ordered concrete steps, each ≤200 chars. */
  subtasks?: string[];
  /** Append `subtasks` under this existing todo instead of creating new. */
  mergeIntoTodoId?: string;
  /** Fallback raw markdown; daemon parses if structured fields are absent. */
  markdown?: string;
}) => Promise<
  { ok: true; todoId: string; mode: "created" | "merged" } | { ok: false; error: string }
>;

/**
 * Callback that marks a todo as completed (sets `completedAt`) when an agent
 * decides — at the end of its session — that the work tied to a todo is done
 * and the user has greenlit clearing it.
 *
 * Invoked from the `complete_todo` MCP tool. The agent supplies the todoId it
 * was originally spawned against (carried in its initial prompt footer).
 */
export type CompleteTodoFromAgent = (input: {
  cwd: string;
  todoId: string;
  report?: string;
  agentId?: string | null;
}) => Promise<{ ok: true } | { ok: false; error: string }>;

/** Callback that lists the open todos for the workspace the agent runs in. */
export type ListTodosForAgent = (input: { cwd: string }) => Promise<
  | {
      ok: true;
      todos: Array<{
        id: string;
        title: string;
        subtasks: Array<{ id: string; text: string; completed: boolean }>;
      }>;
    }
  | { ok: false; error: string }
>;

export type AgentManagerOptions = {
  clients?: Partial<Record<AgentProvider, AgentClient>>;
  idFactory?: () => string;
  registry?: AgentStorage;
  onAgentAttention?: AgentAttentionCallback;
  durableTimelineStore?: AgentTimelineStore;
  terminalManager?: TerminalManager | null;
  /**
   * Base URL for the /mcp/appostle handoff toolbelt endpoint. When set, every
   * agent receives `mcpServers.appostle = { type: "http", url: <base>?callerAgentId=<id> }`.
   * Updated at runtime via `setAppostleMcpBaseUrl` once the HTTP server is bound.
   */
  appostleMcpBaseUrl?: string;
  appostleRuntimeContextProvider?: (() => Promise<string | null>) | null;
  chromeEnabled?: boolean;
  playwrightEnabled?: boolean;
  agentStreamCoalesceWindowMs?: number;
  logger: Logger;
  /** Auth-server userId of the daemon owner. Used to determine if a spawning user needs a separate Claude profile. */
  ownerUserId?: string;
  /**
   * Per-user Claude OAuth tokens. When a guest (verified userId differs from the
   * daemon owner) spawns a Claude Code agent, their stored token is injected as
   * `CLAUDE_CODE_OAUTH_TOKEN` so the agent runs under their own account. Absent
   * on daemons not linked to an auth-server.
   */
  claudeTokenStore?: ClaudeTokenStore;
  /**
   * When true, a guest spawning a Claude agent without their own stored token is
   * rejected with `ClaudeAccountRequiredError`. When false (the default while
   * pre-launch), the guard is parked: a tokenless guest falls back to the owner's
   * Claude account. Guests that *have* added a token always run under it
   * regardless of this flag. Re-enable (set true) before launch.
   */
  requireGuestClaudeToken?: boolean;
  /**
   * Fires after `shareSession` persists a new sharing grant. Lets
   * bootstrap.ts call into the auth-server (via `createSharedSessionTab`)
   * to materialise the recipient's tab in their `sharedWithMe` stack
   * immediately, instead of waiting for the next daemon-boot backfill.
   *
   * Fire-and-forget on AgentManager's side — errors are caught and
   * logged so a transient auth-server hiccup doesn't break the
   * primary share path (which is the in-memory grant + emit).
   */
  onShareCreated?: (params: {
    agentId: string;
    agentCwd: string;
    agentTitle: string | null;
    agentOwnerUserId: string;
    recipientUserId: string;
    orgId: string;
    agentArchived: boolean;
  }) => Promise<void>;
  /**
   * Fires after `archiveAgent` persists. Drives the global archive
   * fan-out: owner's tab → ARCHIVED, every sharee's tab → DELETED.
   * `sharees` is captured BEFORE archival (the agent's sessionShares
   * at the moment archive was triggered).
   */
  onAgentArchived?: (params: {
    agentId: string;
    agentCwd: string;
    agentOwnerUserId: string;
    sharees: ReadonlyArray<{ userId: string; orgId: string }>;
  }) => Promise<void>;
  /**
   * Fires after an archived agent is revived (`unarchiveAgentState`).
   * Mirror of `onAgentArchived`: the owner's tab must flip back to LIVE
   * and every sharee (whose tab was DELETED at archive time) needs a
   * fresh "Shared with me" tab, or the restored agent lands on a stale
   * tab strip everywhere except the restoring device.
   */
  onAgentUnarchived?: (params: {
    agentId: string;
    agentCwd: string;
    agentOwnerUserId: string;
    agentTitle: string | null;
    provider: AgentProvider;
    sharees: ReadonlyArray<{ userId: string; orgId: string }>;
  }) => Promise<void>;
  /**
   * Fires after `updateAgentMetadata` flips `internal` from true → false
   * (promote). Lets bootstrap.ts materialise a tab on the auth-server for
   * the now-visible agent — internal agents never get tabs, so without
   * this a promoted agent would be invisible in every tab strip.
   *
   * Fire-and-forget on AgentManager's side — errors are caught and logged.
   */
  onAgentPromoted?: (params: {
    agentId: string;
    agentCwd: string;
    agentOwnerUserId: string;
    agentTitle: string | null;
    provider: AgentProvider;
  }) => Promise<void>;
  /**
   * Fires after `unshareSession` persists. Drives the revoke fan-out:
   * only the revoked sharee's tab is removed. Owner is unaffected.
   */
  onShareRevoked?: (params: {
    agentId: string;
    agentCwd: string;
    agentOwnerUserId: string;
    revokedUserId: string;
  }) => Promise<void>;
  /**
   * HTTP client to the auth-server's `/daemon/sessions/*` endpoints. When
   * present, every `emitState` triggers a debounced upsert of the agent's
   * session metadata so the cloud-side projection (which powers the cross-
   * host Recents screen) stays current without round-tripping the daemon.
   *
   * Optional + nullable — daemons that aren't linked to an auth-server
   * (older installs, dev-mode without /daemon/link) pass `null` and the
   * fan-out is a silent no-op. Errors on the wire are caught and logged;
   * they never propagate into agent work.
   */
  sessionClient?: AuthServerSessionClient | null;
  /**
   * Daemon-side workspace + project registries. Used by the session fan-
   * out to resolve `cwd → workspaceLabel` and `projectId → projectName`
   * so the cloud-side projection rows actually have something to render
   * (instead of just the raw cwd path). Both are file-backed but fully
   * cached in memory after first load — the per-fan-out lookups are
   * effectively synchronous.
   */
  workspaceRegistry?: WorkspaceRegistry | null;
};

export type WaitForAgentOptions = {
  signal?: AbortSignal;
  waitForActive?: boolean;
};

export type WaitForAgentResult = {
  status: AgentLifecycleStatus;
  permission: AgentPermissionRequest | null;
  lastMessage: string | null;
};

export type WaitForAgentStartOptions = {
  signal?: AbortSignal;
};

type AttentionState =
  | { requiresAttention: false }
  | {
      requiresAttention: true;
      attentionReason: "finished" | "error" | "permission";
      attentionTimestamp: Date;
    };

type ForegroundTurnWaiter = {
  turnId: string;
  callback: (event: AgentStreamEvent) => void;
  settled: boolean;
  settledPromise: Promise<void>;
  resolveSettled: () => void;
};

type PendingForegroundRun = {
  token: string;
  started: boolean;
  settled: boolean;
  settledPromise: Promise<void>;
  resolveSettled: () => void;
};

type ManagedAgentBase = {
  id: string;
  provider: AgentProvider;
  cwd: string;
  capabilities: AgentCapabilityFlags;
  config: AgentSessionConfig;
  runtimeInfo?: AgentRuntimeInfo;
  createdAt: Date;
  updatedAt: Date;
  availableModes: AgentMode[];
  features?: AgentFeature[];
  currentModeId: string | null;
  pendingPermissions: Map<string, AgentPermissionRequest>;
  bufferedPermissionResolutions: Map<
    string,
    Extract<AgentStreamEvent, { type: "permission_resolved" }>
  >;
  inFlightPermissionResponses: Set<string>;
  pendingReplacement: boolean;
  persistence: AgentPersistenceHandle | null;
  historyPrimed: boolean;
  lastUserMessageAt: Date | null;
  lastAssistantMessageAt: Date | null;
  /**
   * Display title for the session. Sourced from `StoredAgentRecord.title`
   * on agent load and kept in sync by `setTitle()`. Cached here so the
   * session-projection fan-out can read it synchronously without a
   * registry round-trip on every emitState. Null until the first title
   * is assigned (auto-generated from the first prompt, or user-set).
   */
  title: string | null;
  lastUsage?: AgentUsage;
  lastError?: string;
  /**
   * Outcome of the most recently completed turn. Used by `setupFinishNotification`
   * (mcp-shared.ts) to distinguish a user interrupt (`"canceled"`) from a natural
   * finish (`"completed"`) — both transition the agent to `lifecycle: "idle"`,
   * which would otherwise look identical to subscribers. Transient: only the
   * latest value is meaningful, and the field is not part of the persisted
   * record or the wire `AgentSnapshotPayload`.
   */
  lastTurnOutcome?: "completed" | "canceled" | "failed";
  attention: AttentionState;
  foregroundTurnWaiters: Set<ForegroundTurnWaiter>;
  unsubscribeSession: (() => void) | null;
  /**
   * Internal agents are hidden from listings and don't trigger notifications.
   */
  internal?: boolean;
  /**
   * Set when this agent has been archived. Persisted to the registry; mirrored
   * here so the live `agent_state` broadcast can carry it to all paired clients
   * — without this, mobile devices never learn that desktop archived an agent
   * and the tab/sidebar entry sticks around indefinitely.
   */
  archivedAt?: string | null;
  /**
   * User-defined labels for categorizing agents (e.g., { surface: "workspace" }).
   */
  labels: Record<string, string>;
  /**
   * Fork lineage. Set when this agent was created via `forkAgent`. The
   * client's tab descriptor reads `parentAgentId` to draw the Split glyph
   * marking the agent as a fork. Stored on the persistence record and
   * rehydrated on resume so the marker survives daemon restart.
   */
  parentAgentId?: string;
  forkedFromMessageUuid?: string;
  /**
   * The auth-server user-id that owns this agent. Set at create-agent time
   * from the creating session's `ownerUserId`. `null` for agents created on
   * a daemon without auth-server linkage (regime A / single-tenant) — those
   * remain visible to every session, preserving the legacy behavior.
   *
   * Multi-tenant filtering rule: a session sees this agent iff
   *   `ownerUserId === null`               // legacy / unscoped (fails closed)
   *   || `ownerUserId === session.ownerUserId`  // owner match
   */
  ownerUserId: string | null;
  /**
   * Owner's display username — used at create time by `buildLaunchContext`
   * to point Claude Code at the right `CLAUDE_CONFIG_DIR`. Persisted so the
   * resume path can do the same routing after a daemon restart. Null when
   * unknown (single-tenant daemons, legacy agents).
   */
  ownerUsername: string | null;
  /**
   * @deprecated Phase 7 — removed. Tab stack assignment now lives on
   * RemoteTab.stackId (auth-server). This field is kept as `null` on live
   * agent records only to satisfy StoredAgentRecord backward-compat reads;
   * it is no longer written or emitted in snapshots.
   */
  stackId: null;
  /**
   * Per-session share grants. Each entry allows a specific user (optionally
   * scoped to an org context) to access this agent. Owner-only mutation.
   * Never synced to the auth-server — daemon-only.
   */
  sessionShares?: Array<{ userId: string; orgId: string; grantedAt: string }>;
};

type ManagedAgentWithSession = ManagedAgentBase & {
  session: AgentSession;
};

type ManagedAgentInitializing = ManagedAgentWithSession & {
  lifecycle: "initializing";
  activeForegroundTurnId: null;
};

type ManagedAgentIdle = ManagedAgentWithSession & {
  lifecycle: "idle";
  activeForegroundTurnId: null;
};

type ManagedAgentRunning = ManagedAgentWithSession & {
  lifecycle: "running";
  activeForegroundTurnId: string | null;
};

type ManagedAgentError = ManagedAgentWithSession & {
  lifecycle: "error";
  activeForegroundTurnId: null;
  lastError: string;
};

type ManagedAgentClosed = ManagedAgentBase & {
  lifecycle: "closed";
  session: null;
  activeForegroundTurnId: null;
};

export type ManagedAgent =
  | ManagedAgentInitializing
  | ManagedAgentIdle
  | ManagedAgentRunning
  | ManagedAgentError
  | ManagedAgentClosed;

export interface AgentMetricsSnapshot {
  total: number;
  byLifecycle: Record<string, number>;
  withActiveForegroundTurn: number;
  timelineStats: {
    totalItems: number;
    maxItemsPerAgent: number;
  };
}

type ActiveManagedAgent =
  | ManagedAgentInitializing
  | ManagedAgentIdle
  | ManagedAgentRunning
  | ManagedAgentError;

type LiveManagedAgent = ActiveManagedAgent;

const SYSTEM_ERROR_PREFIX = "[System Error]";

function attachPersistenceCwd(
  handle: AgentPersistenceHandle | null,
  cwd: string,
): AgentPersistenceHandle | null {
  if (!handle) {
    return null;
  }
  return {
    ...handle,
    metadata: {
      ...(handle.metadata ?? {}),
      cwd,
    },
  };
}

type SubscriptionRecord = {
  callback: AgentSubscriber;
  agentId: string | null;
};

const BUSY_STATUSES: AgentLifecycleStatus[] = ["initializing", "running"];
const AgentIdSchema = z.string().uuid();

function isAgentBusy(status: AgentLifecycleStatus): boolean {
  return BUSY_STATUSES.includes(status);
}

function isTurnTerminalEvent(event: AgentStreamEvent): boolean {
  return (
    event.type === "turn_completed" ||
    event.type === "turn_failed" ||
    event.type === "turn_canceled"
  );
}

function createAbortError(signal: AbortSignal | undefined, fallbackMessage: string): Error {
  const reason = signal?.reason;
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : fallbackMessage;
  return Object.assign(new Error(message), { name: "AbortError" });
}

function validateAgentId(agentId: string, source: string): string {
  const result = AgentIdSchema.safeParse(agentId);
  if (!result.success) {
    throw new Error(`${source}: agentId must be a UUID`);
  }
  return result.data;
}

function normalizeMessageId(messageId: string | undefined): string | undefined {
  if (typeof messageId !== "string") {
    return undefined;
  }
  const trimmed = messageId.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Visibility predicate for multi-tenant session isolation. Returns true iff
 * the requester is allowed to see / mutate this agent.
 *
 *   - `agent.ownerUserId === requester`      → owner
 *
 * After the boot-time migration stamps every record with the host owner's
 * userId, a null owner can only exist as a corrupt or unmigrated entry —
 * and corruption MUST fail closed.
 */
export function canUserAccessAgent(
  agent: Pick<ManagedAgent, "ownerUserId"> & {
    sessionShares?: Array<{ userId: string; orgId: string; grantedAt: string }>;
  },
  requesterUserId: string,
): boolean {
  if (agent.ownerUserId === null) return false;
  if (agent.ownerUserId === requesterUserId) return true;
  if (agent.sessionShares?.some((s) => s.userId === requesterUserId)) return true;
  return false;
}

export class AgentManager {
  private readonly clients = new Map<AgentProvider, AgentClient>();
  private readonly agents = new Map<string, LiveManagedAgent>();
  private readonly timelineStore = new InMemoryAgentTimelineStore();
  private readonly agentsAwaitingInitialSnapshotPersist = new Set<string>();
  private readonly sessionEventTails = new Map<string, Promise<void>>();
  private readonly pendingForegroundRuns = new Map<string, PendingForegroundRun>();
  private readonly subscribers = new Set<SubscriptionRecord>();
  private readonly idFactory: () => string;
  private readonly registry?: AgentStorage;
  private readonly durableTimelineStore?: AgentTimelineStore;
  private readonly previousStatuses = new Map<string, AgentLifecycleStatus>();
  private readonly backgroundTasks = new Set<Promise<void>>();
  private readonly agentStreamCoalescer: AgentStreamCoalescer;
  private appostleMcpBaseUrl: string | null;
  private readonly appostleRuntimeContextProvider: (() => Promise<string | null>) | null;
  public chromeEnabled: boolean;
  public playwrightEnabled: boolean;
  private readonly ownerUserId: string | null;
  private readonly claudeTokenStore: ClaudeTokenStore | null;
  private readonly requireGuestClaudeToken: boolean;
  private onAgentAttention?: AgentAttentionCallback;
  private onShareCreated?: AgentManagerOptions["onShareCreated"];
  private onAgentArchived?: AgentManagerOptions["onAgentArchived"];
  private onAgentUnarchived?: AgentManagerOptions["onAgentUnarchived"];
  private onAgentPromoted?: AgentManagerOptions["onAgentPromoted"];
  private onShareRevoked?: AgentManagerOptions["onShareRevoked"];
  /**
   * Auth-server session projection client. Null when this daemon isn't
   * linked to an auth-server — fan-out becomes a no-op everywhere.
   */
  private sessionClient: AuthServerSessionClient | null;
  /**
   * Registries used to enrich the fan-out payload with human display
   * strings (project name, workspace label). Null in awaiting_link mode
   * before they're constructed; lookups skip cleanly when missing.
   */
  private workspaceRegistry: WorkspaceRegistry | null;
  /**
   * Per-agent debounce handles for session fan-out. emitState fires often
   * (every status flicker, every permission resolve, every turn step), but
   * the auth-server only needs the latest state — coalesce ~750ms of edits
   * into a single HTTP upsert.
   */
  private readonly sessionFanOutTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * Per-agent retry bookkeeping for the tab-runtime fan-out. `attempt`
   * drives the exponential backoff after retryable failures; `notFoundCount`
   * caps how often we chase a 404 (tab may legitimately never exist for
   * this agent). Cleared on success.
   */
  private readonly sessionFanOutRetryState = new Map<
    string,
    { attempt: number; notFoundCount: number }
  >();
  /**
   * Ordering guard: while a runtime patch is in flight for an agent, any
   * new drain request is deferred (flagged in `sessionFanOutRerun`) and
   * re-run after the in-flight request settles. Guarantees the auth-server
   * never applies a stale payload after a fresher one (out-of-order HTTP).
   */
  private readonly sessionFanOutInFlight = new Set<string>();
  private readonly sessionFanOutRerun = new Map<string, ManagedAgent | undefined>();
  private logger: Logger;

  constructor(options: AgentManagerOptions) {
    this.idFactory = options?.idFactory ?? (() => randomUUID());
    this.registry = options?.registry;
    this.durableTimelineStore = options?.durableTimelineStore;
    this.onAgentAttention = options?.onAgentAttention;
    this.onShareCreated = options?.onShareCreated;
    this.onAgentArchived = options?.onAgentArchived;
    this.onAgentUnarchived = options?.onAgentUnarchived;
    this.onAgentPromoted = options?.onAgentPromoted;
    this.onShareRevoked = options?.onShareRevoked;
    this.appostleMcpBaseUrl = options?.appostleMcpBaseUrl ?? null;
    this.appostleRuntimeContextProvider = options?.appostleRuntimeContextProvider ?? null;
    this.chromeEnabled = options?.chromeEnabled ?? true;
    this.playwrightEnabled = options?.playwrightEnabled ?? true;
    this.ownerUserId = options?.ownerUserId ?? null;
    this.claudeTokenStore = options?.claudeTokenStore ?? null;
    this.requireGuestClaudeToken = options?.requireGuestClaudeToken ?? false;
    this.sessionClient = options?.sessionClient ?? null;
    this.workspaceRegistry = options?.workspaceRegistry ?? null;
    this.logger = options.logger.child({ module: "agent", component: "agent-manager" });
    this.agentStreamCoalescer = new AgentStreamCoalescer({
      windowMs: options.agentStreamCoalesceWindowMs ?? AGENT_STREAM_COALESCE_DEFAULT_WINDOW_MS,
      timers: { setTimeout, clearTimeout },
      onFlush: ({ agentId, item, provider, turnId }) => {
        const event = this.recordAndDispatchTimelineItem(agentId, item, provider, turnId);
        this.notifyForegroundTurnWaiters(agentId, event);
      },
    });
    if (options?.clients) {
      for (const [provider, client] of Object.entries(options.clients)) {
        if (client) {
          this.registerClient(provider as AgentProvider, client);
        }
      }
    }
  }

  registerClient(provider: AgentProvider, client: AgentClient): void {
    this.clients.set(provider, client);
  }

  getRegisteredProviderIds(): AgentProvider[] {
    return Array.from(this.clients.keys());
  }

  setAgentAttentionCallback(callback: AgentAttentionCallback): void {
    this.onAgentAttention = callback;
  }

  setAppostleMcpBaseUrl(url: string | null): void {
    this.appostleMcpBaseUrl = url;
  }

  /**
   * Inject Appostle's own MCP servers into a session config keyed by `agentId`.
   * Called on every path that materializes an `AgentSessionConfig` for a live
   * session — initial spawn, resume-from-disk, and hot reload.
   *
   * The Appostle directive toolbelt (handoff, report, talkback, chain, learn,
   * write_plan, todo, cross-host helpers)
   * is served over HTTP at /mcp/appostle via `createAppostleHttpMcpServer`.
   * This HTTP path works for every provider (Claude, OpenCode, Codex).
   */
  private withAppostleMcpServers(config: AgentSessionConfig, agentId: string): AgentSessionConfig {
    if (this.appostleMcpBaseUrl == null && config.mcpServers == null) {
      return config;
    }
    const mcpServers: Record<string, McpServerConfig> = {
      ...(config.mcpServers ?? {}),
    };
    if (this.appostleMcpBaseUrl != null) {
      mcpServers.appostle = {
        type: "http" as const,
        url: `${this.appostleMcpBaseUrl}?callerAgentId=${agentId}`,
        // The Claude SDK defers HTTP MCP tool schemas behind a tool-search
        // discovery step by default. For the Appostle directive toolbelt that breaks
        // the contract: `>handoff` etc. only fire if the model has the
        // schemas in context the moment the user message arrives. Force
        // eager loading so trigger-word routing keeps working post-HTTP-
        // transport swap (was implicit when the server was in-process).
        alwaysLoad: true,
      };
    }
    if (Object.keys(mcpServers).length === 0) {
      return config;
    }
    return { ...config, mcpServers };
  }

  private async withAppostleRuntimeContext(
    config: AgentSessionConfig,
  ): Promise<AgentSessionConfig> {
    if (!this.appostleRuntimeContextProvider) {
      return {
        ...config,
        systemPrompt: appendAppostleRuntimeContext(config.systemPrompt, null),
      };
    }
    try {
      const runtimeContext = await this.appostleRuntimeContextProvider();
      const systemPrompt = appendAppostleRuntimeContext(config.systemPrompt, runtimeContext);
      return systemPrompt ? { ...config, systemPrompt } : { ...config, systemPrompt: undefined };
    } catch (err) {
      this.logger.warn({ err }, "failed to build Appostle runtime context for agent prompt");
      const systemPrompt = appendAppostleRuntimeContext(config.systemPrompt, null);
      return systemPrompt ? { ...config, systemPrompt } : { ...config, systemPrompt: undefined };
    }
  }

  private async withAppostleSessionConfig(
    config: AgentSessionConfig,
    agentId: string,
  ): Promise<AgentSessionConfig> {
    return this.withAppostleRuntimeContext(this.withAppostleMcpServers(config, agentId));
  }

  public getMetricsSnapshot(): AgentMetricsSnapshot {
    const byLifecycle: Record<string, number> = {};
    let withActiveForegroundTurn = 0;
    let totalItems = 0;
    let maxItemsPerAgent = 0;

    for (const agent of this.agents.values()) {
      byLifecycle[agent.lifecycle] = (byLifecycle[agent.lifecycle] ?? 0) + 1;

      if (agent.activeForegroundTurnId !== null) {
        withActiveForegroundTurn++;
      }

      if (!this.timelineStore.has(agent.id)) {
        continue;
      }

      const len = this.timelineStore.getItems(agent.id).length;
      totalItems += len;
      if (len > maxItemsPerAgent) {
        maxItemsPerAgent = len;
      }
    }

    return {
      total: this.agents.size,
      byLifecycle,
      withActiveForegroundTurn,
      timelineStats: {
        totalItems,
        maxItemsPerAgent,
      },
    };
  }

  private touchUpdatedAt(agent: ManagedAgent): Date {
    const nowMs = Date.now();
    const previousMs = agent.updatedAt.getTime();
    const nextMs = nowMs > previousMs ? nowMs : previousMs + 1;
    const next = new Date(nextMs);
    agent.updatedAt = next;
    return next;
  }

  hasInFlightRun(agentId: string): boolean {
    const agent = this.agents.get(agentId);
    if (!agent) {
      return false;
    }

    return (
      agent.lifecycle === "running" ||
      Boolean(agent.activeForegroundTurnId) ||
      this.hasPendingForegroundRun(agentId)
    );
  }

  /**
   * Is an internal agent matching `title` + `cwd` (+ optional owner) currently
   * busy on this daemon? Used by RPCs that want to surface "generation still
   * running" UI after the originating client re-mounted and lost its in-memory
   * promise — e.g. closing/reopening the brand modal while the brand-token
   * generator is mid-run. "Busy" means initialising or running; idle/error/
   * closed do not count.
   *
   * Internal agents are filtered out of `listAgents()` so callers can't reach
   * them through the normal listing surface — this is the dedicated escape
   * hatch for the few subsystems that legitimately need to detect a specific
   * in-flight internal job by (title, cwd).
   */
  hasActiveInternalAgent(query: {
    title: string;
    cwd: string;
    ownerUserId?: string | null;
  }): boolean {
    for (const agent of this.agents.values()) {
      if (!agent.internal) continue;
      if (agent.title !== query.title) continue;
      if (agent.cwd !== query.cwd) continue;
      if (
        query.ownerUserId !== undefined &&
        agent.ownerUserId !== null &&
        agent.ownerUserId !== query.ownerUserId
      ) {
        continue;
      }
      if (agent.lifecycle === "initializing" || agent.lifecycle === "running") {
        return true;
      }
    }
    return false;
  }

  subscribe(callback: AgentSubscriber, options?: SubscribeOptions): () => void {
    const targetAgentId =
      options?.agentId == null ? null : validateAgentId(options.agentId, "subscribe");
    const record: SubscriptionRecord = {
      callback,
      agentId: targetAgentId,
    };
    this.subscribers.add(record);

    if (options?.replayState !== false) {
      if (record.agentId) {
        const agent = this.agents.get(record.agentId);
        if (agent) {
          callback({
            type: "agent_state",
            agent: { ...agent },
          });
        }
      } else {
        // For global subscribers, skip internal agents during replay
        for (const agent of this.agents.values()) {
          if (agent.internal) {
            continue;
          }
          callback({
            type: "agent_state",
            agent: { ...agent },
          });
        }
      }
    }

    return () => {
      this.subscribers.delete(record);
    };
  }

  listAgents(): ManagedAgent[] {
    return Array.from(this.agents.values())
      .filter((agent) => !agent.internal)
      .map((agent) => ({
        ...agent,
      }));
  }

  /**
   * Same as `listAgents()` but applies the multi-tenant visibility predicate.
   * Pass the connecting session's `ownerUserId`; agents are kept iff the
   * agent owner matches the requester. Internal agents are still filtered out.
   */
  listAgentsForUser(requesterUserId: string): ManagedAgent[] {
    const all = this.listAgents();
    return all.filter((agent) => canUserAccessAgent(agent, requesterUserId));
  }

  /**
   * Predicate the per-agent RPCs use to reject cross-user writes (send,
   * cancel, delete, fork, etc.). Identical visibility rule as
   * `listAgentsForUser` — kept as a free function below for reuse by Session.
   */
  canUserAccessAgentById(agentId: string, requesterUserId: string): boolean {
    const agent = this.agents.get(agentId);
    if (!agent) return false;
    return canUserAccessAgent(agent, requesterUserId);
  }

  async listPersistedAgents(
    options?: PersistedAgentQueryOptions,
  ): Promise<PersistedAgentDescriptor[]> {
    if (options?.provider) {
      const client = this.requireClient(options.provider);
      if (!client.listPersistedAgents) {
        return [];
      }
      return client.listPersistedAgents({ limit: options.limit });
    }

    const descriptors: PersistedAgentDescriptor[] = [];
    for (const [provider, client] of this.clients.entries()) {
      if (!client.listPersistedAgents) {
        continue;
      }
      try {
        const entries = await client.listPersistedAgents({
          limit: options?.limit,
        });
        descriptors.push(...entries);
      } catch (error) {
        this.logger.warn({ err: error, provider }, "Failed to list persisted agents for provider");
      }
    }

    const limit = options?.limit ?? 20;
    return descriptors
      .sort((a, b) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime())
      .slice(0, limit);
  }

  async listProviderAvailability(): Promise<ProviderAvailability[]> {
    const checks = Array.from(this.clients.keys()).map(async (provider) => {
      const client = this.clients.get(provider);
      if (!client) {
        return {
          provider,
          available: false,
          error: `No client registered for provider '${provider}'`,
        } satisfies ProviderAvailability;
      }

      try {
        const available = await client.isAvailable();
        return {
          provider,
          available,
          error: null,
        } satisfies ProviderAvailability;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        this.logger.warn({ err: error, provider }, "Failed to check provider availability");
        return {
          provider,
          available: false,
          error: message,
        } satisfies ProviderAvailability;
      }
    });

    return Promise.all(checks);
  }

  async listDraftCommands(config: AgentSessionConfig): Promise<AgentSlashCommand[]> {
    const normalizedConfig = await this.normalizeConfig(config);

    // Claude: skip the ~6s "spawn-just-to-list" round-trip by reading skills
    // directly from disk. The SDK's `q.supportedCommands()` adds built-in
    // commands (/clear, /help, /model, …) on top of these — those reappear
    // as soon as the real session spawns on first user message.
    if (normalizedConfig.provider === "claude") {
      return enumerateClaudeDraftCommands(normalizedConfig.cwd);
    }

    const client = this.requireClient(normalizedConfig.provider);
    const available = await client.isAvailable();
    if (!available) {
      throw new Error(
        `Provider '${normalizedConfig.provider}' is not available. Please ensure the CLI is installed.`,
      );
    }

    const session = await client.createSession(normalizedConfig);
    try {
      if (!session.listCommands) {
        throw new Error(
          `Provider '${normalizedConfig.provider}' does not support listing commands`,
        );
      }
      return await session.listCommands();
    } finally {
      try {
        await session.close();
      } catch (error) {
        this.logger.warn(
          { err: error, provider: normalizedConfig.provider },
          "Failed to close draft command listing session",
        );
      }
    }
  }

  async listDraftFeatures(config: AgentSessionConfig): Promise<AgentFeature[]> {
    const normalizedConfig = await this.normalizeConfig(config);
    const client = this.requireClient(normalizedConfig.provider);
    const available = await client.isAvailable();
    if (!available) {
      throw new Error(
        `Provider '${normalizedConfig.provider}' is not available. Please ensure the CLI is installed.`,
      );
    }

    const session = await client.createSession(normalizedConfig);
    try {
      return session.features ?? [];
    } finally {
      try {
        await session.close();
      } catch (error) {
        this.logger.warn(
          { err: error, provider: normalizedConfig.provider },
          "Failed to close draft feature listing session",
        );
      }
    }
  }

  /**
   * Returns a shallow CLONE of the agent for safe read-only consumption.
   * Mutating the returned object does NOT affect the live agent stored in
   * `this.agents` — for that, callers must go through a public mutator on
   * `AgentManager` (e.g. `updateAgentMetadata`) or, when
   * truly necessary, use `getLiveAgent(id)` to obtain the live reference.
   */
  getAgentSnapshot(id: string): ManagedAgent | null {
    const agent = this.agents.get(id);
    return agent ? { ...agent } : null;
  }

  /**
   * Returns the LIVE agent reference from the internal map. Use only when
   * mutation is intentional or when the caller relies on identity (e.g.
   * passing into a manager method that writes to the same object). Most
   * callers should prefer `getAgentSnapshot`.
   */
  getLiveAgent(id: string): ManagedAgent | null {
    return this.agents.get(id) ?? null;
  }

  getTimeline(id: string): AgentTimelineItem[] {
    this.requireAgent(id);
    return this.timelineStore.getItems(id);
  }

  async getTimelineRows(id: string): Promise<AgentTimelineRow[]> {
    this.requireAgent(id);
    if (this.durableTimelineStore) {
      return await this.durableTimelineStore.getCommittedRows(id);
    }
    return this.timelineStore.getRows(id);
  }

  fetchTimeline(id: string, options?: AgentTimelineFetchOptions): AgentTimelineFetchResult {
    this.requireAgent(id);
    return this.timelineStore.fetch(id, options);
  }

  async createAgent(
    config: AgentSessionConfig,
    agentId?: string,
    options?: {
      labels?: Record<string, string>;
      workspaceId?: string;
      initialPrompt?: string;
      /**
       * The auth-server user-id of the connecting session that's creating
       * this agent. Stamped onto the ManagedAgent so per-user filtering
       * shows it only to that user (and anyone explicitly shared with
       * later). Required when `options` is provided — sessions are always
       * account-bound after the single-tenant fallback was removed. If
       * `options` is omitted entirely (some test paths), the agent gets a
       * placeholder owner id derived from the manager's configured owner
       * if any, else throws.
       */
      ownerUserId: string;
      /** Self-declared auth-server userId (metadata; not used for ACL). */
      userId?: string;
      /** Display name of the user (used for Claude profile dir). */
      username?: string;
      /**
       * @deprecated Phase 7 — removed. Tab stacks are now on auth-server.
       * Accepted but ignored so old call-sites compile during transition.
       */
      stackId?: string | null;
      /**
       * Per-session share grants to pre-stamp on the new agent. When
       * spawning a child from a shared parent (e.g. handoff), pass the
       * parent's `sessionShares` here so sharees can immediately see the
       * child agent.
       */
      sessionShares?: Array<{ userId: string; orgId: string; grantedAt: string }>;
    },
  ): Promise<ManagedAgent> {
    // The display username is also stamped onto the agent record as
    // `ownerUsername` so the resume path can re-route Claude profile dirs
    // after a daemon restart. Done here rather than at the registerSession
    // call site to avoid threading the option through every overload.
    const resolvedAgentId = validateAgentId(agentId ?? this.idFactory(), "createAgent");
    const injectedConfig = await this.withAppostleSessionConfig(config, resolvedAgentId);
    const normalizedConfig = await this.normalizeConfig(injectedConfig);
    // After the single-tenant fallback was removed, every spawned agent
    // MUST carry an owner. Production callers always pass
    // `options.ownerUserId` (websocket-server resolves the peer's pubkey to
    // a userId before any session can create agents). Test paths that omit
    // `options` entirely fall back to the manager's configured `ownerUserId`
    // (passed at construction) and finally to a deterministic placeholder
    // so existing AgentManager unit tests keep compiling. Production code
    // never hits the placeholder branch because the websocket-server gate
    // rejects unresolvable peers (1008) before they can reach createAgent.
    const resolvedOwnerUserId =
      options?.ownerUserId ?? this.ownerUserId ?? AGENT_MANAGER_TEST_OWNER_PLACEHOLDER;
    const launchContext = this.buildLaunchContext(resolvedAgentId, {
      connectingUserId: resolvedOwnerUserId,
      provider: normalizedConfig.provider,
    });
    const client = this.requireClient(normalizedConfig.provider);
    const available = await client.isAvailable();
    if (!available) {
      throw new Error(
        `Provider '${normalizedConfig.provider}' is not available. Please ensure the CLI is installed.`,
      );
    }
    const session = await client.createSession(normalizedConfig, launchContext);
    return this.registerSession(session, normalizedConfig, resolvedAgentId, {
      labels: options?.labels,
      workspaceId: options?.workspaceId,
      ownerUserId: resolvedOwnerUserId,
      ownerUsername: options?.username ?? null,
      stackId: null,
      sessionShares: options?.sessionShares,
    });
  }

  /**
   * Spawn a child agent that inherits identity context from a parent agent.
   *
   * Centralises the inheritance rules so every spawner (handoff, future
   * quest workers that have a parent agent, etc.) picks them up for free:
   *
   * - `ownerUserId`   — inherited from parent
   * - `sessionShares` — **inherited** (the bug fix: sharees see child agents)
   * - `cwd`           — inherited unless overridden by config
   *
   * NOTE: When the parent has `sessionShares`, the child inherits them. On
   * the recipient's side, the child Tab is created in their "Shared with
   * me" stack of the same project via the daemon's `onShareCreated`
   * fan-out in bootstrap.ts → `createSharedSessionTab`. That path is the
   * sibling of the `onHandoffSpawned` callback used to materialise the
   * owner's own tab in the parent's stack.
   */
  async spawnAgentFromParent(
    parent: Pick<
      ManagedAgent,
      | "ownerUserId"
      | "ownerUsername"
      | "sessionShares"
      | "cwd"
      | "provider"
      | "config"
      | "currentModeId"
    >,
    childConfig: AgentSessionConfig,
    options?: {
      labels?: Record<string, string>;
      workspaceId?: string;
      agentId?: string;
    },
  ): Promise<ManagedAgent> {
    if (!parent.ownerUserId) {
      throw new Error("spawnAgentFromParent: parent agent has no resolved owner");
    }
    return this.createAgent(childConfig, options?.agentId, {
      ownerUserId: parent.ownerUserId,
      userId: parent.ownerUserId,
      ...(parent.ownerUsername ? { username: parent.ownerUsername } : {}),
      // Deep-copy shares so mutations to the child's list don't affect parent.
      sessionShares: parent.sessionShares ? parent.sessionShares.map((s) => ({ ...s })) : undefined,
      labels: options?.labels,
      workspaceId: options?.workspaceId,
    });
  }

  /**
   * Fork a Claude agent at a specific user-turn message UUID.
   *
   * Truncates the parent's session JSONL strictly BEFORE the chosen user
   * turn (via `forkSession`) and persists a new dormant StoredAgentRecord
   * pointing at the truncated copy. The chosen user message's text is
   * returned so the caller can prefill the new agent's composer with it
   * — the new agent inherits all prior context and the user gets a chance
   * to edit/re-send the dropped message.
   *
   * Forks are allowed while the parent is still running: we only read
   * already-flushed bytes, which POSIX append-only writes do not disturb.
   */
  async forkAgent(params: {
    parentAgentId: string;
    forkPointUuid: string;
    forkPointUserMessageIndex?: number;
  }): Promise<{
    newAgentId: string;
    newSessionId: string;
    droppedUserText: string;
    /**
     * The full StoredAgentRecord that was persisted for the new fork. The
     * session-layer caller projects this into an `AgentSnapshotPayload` and
     * ships it back in `fork_agent_response` so the client can seed its
     * session store before navigating to the new tab — without this seed,
     * the tab opens before any snapshot arrives and the visibility layer
     * places it in the Default stack instead of inheriting the parent's.
     */
    record: StoredAgentRecord;
  }> {
    const storage = this.requireRegistry();
    const parent = await storage.get(params.parentAgentId);
    if (!parent) {
      throw new Error(`Parent agent not found: ${params.parentAgentId}`);
    }
    if (parent.archivedAt) {
      throw new Error(`Cannot fork archived agent: ${params.parentAgentId}`);
    }
    if (parent.provider !== "claude") {
      throw new Error(`Fork only supports the claude provider (got '${parent.provider}')`);
    }

    const parentSessionId = parent.persistence?.sessionId ?? parent.runtimeInfo?.sessionId ?? null;
    if (!parentSessionId) {
      throw new Error(
        `Parent agent has no Claude session id to fork from: ${params.parentAgentId}`,
      );
    }

    // The client may pass a appostle-internal message id rather than the Claude
    // session UUID. When it also supplies the user-message index, translate by
    // reading the session transcript via the SDK.
    const resolvedForkPointUuid = await this.resolveClaudeUserMessageUuid({
      parentSessionId,
      parentCwd: parent.cwd,
      candidateUuid: params.forkPointUuid,
      userMessageIndex: params.forkPointUserMessageIndex,
      agentId: params.parentAgentId,
    });

    const sourcePath = findClaudeSessionFile({ sessionId: parentSessionId });
    if (!sourcePath) {
      throw new Error(
        `Claude session file not found on disk for sessionId '${parentSessionId}'. ` +
          "The session may have been pruned or the claude home dir differs from $HOME/.claude.",
      );
    }
    const newSessionId = randomUUID();
    const targetPath = join(dirname(sourcePath), `${newSessionId}.jsonl`);
    const { droppedUserText } = forkSession({
      sourceSessionPath: sourcePath,
      forkPointUuid: resolvedForkPointUuid,
      newSessionId,
      targetSessionPath: targetPath,
    });

    const newAgentId = validateAgentId(this.idFactory(), "forkAgent");
    const now = new Date().toISOString();
    // Inherit the parent's title verbatim — the Split icon (driven by
    // `parentAgentId`) is the canonical "this is a fork" marker, so a
    // textual "(fork)" suffix would be redundant and noisy in tab bars
    // and sidebars.
    const forkTitle = parent.title ?? parent.config?.title ?? null;
    const newRecord: StoredAgentRecord = {
      ...parent,
      id: newAgentId,
      createdAt: now,
      updatedAt: now,
      lastActivityAt: now,
      lastUserMessageAt: null,
      lastAssistantMessageAt: null,
      lastStatus: "closed",
      lastError: null,
      requiresAttention: false,
      attentionReason: null,
      attentionTimestamp: null,
      archivedAt: null,
      title: forkTitle,
      config: parent.config ? { ...parent.config, title: forkTitle } : parent.config,
      parentAgentId: params.parentAgentId,
      forkedFromMessageUuid: params.forkPointUuid,
      persistence: {
        provider: "claude",
        sessionId: newSessionId,
        nativeHandle: parent.persistence?.nativeHandle,
        metadata: parent.persistence?.metadata,
      },
      runtimeInfo: parent.runtimeInfo
        ? { ...parent.runtimeInfo, sessionId: newSessionId }
        : undefined,
    };

    await storage.upsert(newRecord);
    return { newAgentId, newSessionId, droppedUserText, record: newRecord };
  }

  /**
   * Rewind a Claude agent in place to a previous user message: drop every
   * timeline row from that message onward, swap the agent's Claude session id
   * to a sliced copy of the original transcript, and reload the live session
   * so the next turn resumes from the truncated history.
   *
   * Same agent id, same workspace, same persisted record — only the contents
   * of the conversation tail change. Files are NOT touched; users who want
   * file restoration can still type `/rewind` in the composer.
   *
   * @returns droppedUserText for composer prefill, droppedRowCount for diagnostics
   */
  async rewindAgent(params: {
    agentId: string;
    rewindPointUuid: string;
    rewindPointUserMessageIndex?: number;
  }): Promise<{ droppedUserText: string; droppedRowCount: number }> {
    const storage = this.requireRegistry();
    const stored = await storage.get(params.agentId);
    if (!stored) {
      throw new Error(`Agent not found: ${params.agentId}`);
    }
    if (stored.archivedAt) {
      throw new Error(`Cannot rewind archived agent: ${params.agentId}`);
    }
    if (stored.provider !== "claude") {
      throw new Error(`Rewind only supports the claude provider (got '${stored.provider}')`);
    }

    const sessionId = stored.persistence?.sessionId ?? stored.runtimeInfo?.sessionId ?? null;
    if (!sessionId) {
      throw new Error(`Agent has no Claude session id to rewind: ${params.agentId}`);
    }

    const live = this.agents.get(params.agentId);
    if (live && this.hasInFlightRun(params.agentId)) {
      // Same constraint Claude SDK puts on rewind / fork: in-flight turns
      // mutate the JSONL on disk concurrently, which would race the slicer.
      throw new Error("Cannot rewind while a turn is in flight; cancel it first.");
    }

    const resolvedUuid = await this.resolveClaudeUserMessageUuid({
      parentSessionId: sessionId,
      parentCwd: stored.cwd,
      candidateUuid: params.rewindPointUuid,
      userMessageIndex: params.rewindPointUserMessageIndex,
      agentId: params.agentId,
    });

    const sourcePath = findClaudeSessionFile({ sessionId });
    if (!sourcePath) {
      throw new Error(
        `Claude session file not found on disk for sessionId '${sessionId}'. ` +
          "The session may have been pruned or the claude home dir differs from $HOME/.claude.",
      );
    }
    const newSessionId = randomUUID();
    const targetPath = join(dirname(sourcePath), `${newSessionId}.jsonl`);
    const { droppedUserText } = forkSession({
      sourceSessionPath: sourcePath,
      forkPointUuid: resolvedUuid,
      newSessionId,
      targetSessionPath: targetPath,
    });

    // Truncate the in-memory timeline. The cut is at the rewind-point user
    // message (it gets dropped along with everything after — same semantics as
    // the JSONL slice, where the user-message line and everything after is
    // omitted). Truncation re-mints the epoch so any client cursors against
    // the old timeline are forced to refetch.
    //
    // Pass both ids: rows minted locally via `recordUserMessage` carry the
    // client's optimistic `msg_<ts>_<rand>` id, while rows rehydrated from
    // the Claude JSONL carry the real transcript UUID. The JSONL slice was
    // already keyed off `resolvedUuid`; the in-memory cut must agree, so we
    // give the store both candidates and let it match whichever the row has.
    const droppedRowCount = this.timelineStore.has(params.agentId)
      ? this.timelineStore.truncateAfterUserMessage(params.agentId, [
          params.rewindPointUuid,
          resolvedUuid,
        ])
      : 0;

    // Update the stored record so the new sessionId survives daemon restart.
    const now = new Date().toISOString();
    const updatedRecord: StoredAgentRecord = {
      ...stored,
      updatedAt: now,
      lastUserMessageAt: null,
      lastAssistantMessageAt: null,
      persistence: stored.persistence
        ? { ...stored.persistence, sessionId: newSessionId }
        : { provider: "claude", sessionId: newSessionId },
      runtimeInfo: stored.runtimeInfo
        ? { ...stored.runtimeInfo, sessionId: newSessionId }
        : undefined,
    };
    await storage.upsert(updatedRecord);

    // If the agent is live in memory, mutate its persistence handle so the
    // next reload uses the truncated session, then reload to spawn a fresh
    // Claude query that resumes from the new sessionId. The reload preserves
    // the (now-truncated) timeline because timelineStore already has rows for
    // this agentId — registerSession's seed branch is skipped.
    if (live && live.session) {
      if (live.persistence) {
        live.persistence = { ...live.persistence, sessionId: newSessionId };
      } else {
        live.persistence = { provider: "claude", sessionId: newSessionId };
      }
      if (live.runtimeInfo) {
        live.runtimeInfo = { ...live.runtimeInfo, sessionId: newSessionId };
      }
      await this.reloadAgentSession(params.agentId);
    }

    return { droppedUserText, droppedRowCount };
  }

  /**
   * Resolve the real Claude session UUID for a fork point.
   *
   * The client may pass a appostle-internal id (e.g. "msg_<ts>_<rand>") rather
   * than a Claude UUID — especially for optimistic user messages that haven't
   * been reconciled against the daemon's timeline. When the client supplies
   * `userMessageIndex`, we read the parent's session transcript via the
   * official SDK function and pick the Nth user message.
   */
  private async resolveClaudeUserMessageUuid(params: {
    parentSessionId: string;
    parentCwd: string;
    candidateUuid: string;
    userMessageIndex: number | undefined;
    /**
     * Agent id whose timeline should be consulted for an optimistic-id →
     * Claude-UUID upgrade. When set, and the candidate is an Appostle
     * optimistic id (`msg_…`), we first ask the timeline store whether the
     * row carrying that id has been stamped with a `claudeUuid` (which
     * happens when the SDK echoes the user turn back). If so, we resolve
     * directly to that UUID and skip the index-based JSONL walk entirely.
     */
    agentId?: string;
  }): Promise<string> {
    // Fast path: if the candidate is shaped like a UUID and no transcript
    // lookup is available, trust it. (Legacy CLI callers without index info.)
    const lookLikeUuid = UUID_REGEX.test(params.candidateUuid);
    if (params.userMessageIndex === undefined) {
      if (lookLikeUuid) {
        return params.candidateUuid;
      }
      throw new Error(
        `Fork point '${params.candidateUuid}' is not a Claude UUID and no userMessageIndex was supplied for resolution.`,
      );
    }

    // Before any transcript I/O: if the candidate is an Appostle optimistic
    // id and the timeline store has already stamped a `claudeUuid` onto that
    // row (from the SDK echo dedup path), use it directly. This is the
    // dominant case for the typical "fork my last message" flow and bypasses
    // the index-resolution path entirely — no index drift possible.
    if (!lookLikeUuid && params.agentId && this.timelineStore.has(params.agentId)) {
      const upgradedUuid = this.timelineStore.getClaudeUuidForUserMessage(
        params.agentId,
        params.candidateUuid,
      );
      if (upgradedUuid) {
        return upgradedUuid;
      }
    }

    // Read the transcript once and use it for both direct lookup and index
    // fallback. Index counts user-text turns ONLY. Claude's transcript stores
    // tool_result payloads as type:"user" messages — those must NOT count,
    // otherwise the index drifts wildly vs the rendered timeline. Also skip
    // "transcript noise" (interrupt / no-response / local-command-stdout
    // placeholders) since the renderer hides those too.
    const messages = await getSessionMessages(params.parentSessionId, {
      dir: params.parentCwd,
    });
    const userUuids: string[] = [];
    let directMatch = false;
    for (const msg of messages) {
      if (msg.type !== "user" || typeof msg.uuid !== "string") continue;
      if (isToolResultUserMessage(msg.message)) continue;
      const content = (msg.message as { content?: unknown } | undefined)?.content;
      if (isClaudeTranscriptNoiseContent(content)) continue;
      if (lookLikeUuid && msg.uuid === params.candidateUuid) {
        directMatch = true;
      }
      userUuids.push(msg.uuid);
    }

    // Prefer direct UUID match when present — most reliable, immune to
    // timeline/JSONL count drift (e.g. when the daemon's in-memory timeline
    // has both an optimistic recordUserMessage row and an SDK echo row for
    // the same turn, but the JSONL only has the SDK-side line).
    if (directMatch) {
      return params.candidateUuid;
    }

    const uuid = userUuids[params.userMessageIndex];
    if (typeof uuid !== "string") {
      throw new Error(
        `userMessageIndex ${params.userMessageIndex} out of range (${userUuids.length} user messages in session ${params.parentSessionId})`,
      );
    }
    return uuid;
  }

  // Reconstruct an agent from provider persistence. Callers should explicitly
  // hydrate timeline history after resume.
  async resumeAgentFromPersistence(
    handle: AgentPersistenceHandle,
    overrides?: Partial<AgentSessionConfig>,
    agentId?: string,
    options?: {
      createdAt?: Date;
      updatedAt?: Date;
      lastUserMessageAt?: Date | null;
      lastAssistantMessageAt?: Date | null;
      title?: string | null;
      labels?: Record<string, string>;
      parentAgentId?: string;
      forkedFromMessageUuid?: string;
      /**
       * Multi-tenant ownership stamps loaded from `StoredAgentRecord`. Null /
       * empty for pre-Phase-2c records — agent rehydrates as unscoped
       * (visible to every connecting user). The caller (rehydrate path in
       * session.ts / quest runner) is responsible for forwarding these
       * values from the stored record.
       */
      ownerUserId?: string | null;
      ownerUsername?: string | null;
      /**
       * Tab stack assignment loaded from the stored record. Round-tripped
       * so a daemon restart keeps an agent in the same stack it was in
       * when the daemon went down. `null` = unstacked (Default).
       */
      stackId?: string | null;
    },
  ): Promise<ManagedAgent> {
    const resolvedAgentId = validateAgentId(
      agentId ?? this.idFactory(),
      "resumeAgentFromPersistence",
    );
    const metadata = (handle.metadata ?? {}) as Partial<AgentSessionConfig>;
    const mergedConfig = await this.withAppostleSessionConfig(
      {
        ...metadata,
        ...overrides,
        provider: handle.provider,
      } as AgentSessionConfig,
      resolvedAgentId,
    );
    const normalizedConfig = await this.normalizeConfig(mergedConfig);
    // Pass the injected MCP servers through to the provider so its internal
    // config (rebuilt from handle.metadata + overrides) carries the in-process
    // handoff server. Otherwise the spawned subprocess starts with no
    // mcp__appostle__handoff tool even though our agent.config has it.
    const overridesWithMcp: Partial<AgentSessionConfig> = {
      ...(overrides ?? {}),
      mcpServers: mergedConfig.mcpServers,
    };
    const resumeOverrides =
      normalizedConfig.model !== mergedConfig.model
        ? { ...overridesWithMcp, model: normalizedConfig.model }
        : overridesWithMcp;
    // Re-route to the original guest's own Claude account if they were a
    // non-owner user. `ownerUserId` comes from the persisted record via
    // `extractTimestamps` — null for legacy agents (falls through to the daemon
    // owner's account, same as before).
    const launchContext = this.buildLaunchContext(resolvedAgentId, {
      connectingUserId: options?.ownerUserId ?? null,
      provider: handle.provider,
    });
    const client = this.requireClient(handle.provider);
    const available = await client.isAvailable();
    if (!available) {
      throw new Error(
        `Provider '${handle.provider}' is not available. Please ensure the CLI is installed.`,
      );
    }
    const session = await client.resumeSession(handle, resumeOverrides, launchContext);
    return this.registerSession(session, normalizedConfig, resolvedAgentId, options);
  }

  // Hot-reload an active agent session with config overrides while preserving
  // in-memory timeline state.
  async reloadAgentSession(
    agentId: string,
    overrides?: Partial<AgentSessionConfig>,
  ): Promise<ManagedAgent> {
    let existing = this.requireSessionAgent(agentId);
    if (this.hasInFlightRun(agentId)) {
      await this.cancelAgentRun(agentId);
      existing = this.requireSessionAgent(agentId);
    }
    const preservedHistoryPrimed = existing.historyPrimed;
    const preservedLastUsage = existing.lastUsage;
    const preservedLastError = existing.lastError;
    const preservedAttention = existing.attention;
    const handle = existing.persistence;
    const provider = handle?.provider ?? existing.provider;
    const client = this.requireClient(provider);
    const refreshConfig = await this.withAppostleSessionConfig(
      {
        ...existing.config,
        ...overrides,
        provider,
      } as AgentSessionConfig,
      agentId,
    );
    const normalizedConfig = await this.normalizeConfig(refreshConfig);
    const launchContext = this.buildLaunchContext(agentId, {
      connectingUserId: existing.ownerUserId ?? null,
      provider,
    });

    const session = handle
      ? await client.resumeSession(handle, normalizedConfig, launchContext)
      : await client.createSession(normalizedConfig, launchContext);

    this.agentStreamCoalescer.flushAndDiscard(agentId);
    // Remove the existing agent entry before swapping sessions
    this.agents.delete(agentId);
    if (existing.unsubscribeSession) {
      existing.unsubscribeSession();
      existing.unsubscribeSession = null;
    }
    for (const waiter of existing.foregroundTurnWaiters) {
      this.settleForegroundTurnWaiter(waiter);
    }
    existing.foregroundTurnWaiters.clear();
    this.settlePendingForegroundRun(agentId);
    try {
      await existing.session.close();
    } catch (error) {
      this.logger.warn({ err: error, agentId }, "Failed to close previous session during refresh");
    }

    // Preserve existing labels and timeline during reload.
    return this.registerSession(session, normalizedConfig, agentId, {
      labels: existing.labels,
      // Preserve the ownership stamp across reload — refreshing an agent
      // must never silently re-attribute it to whoever happens to be
      // connected at refresh time.
      ownerUserId: existing.ownerUserId,
      ownerUsername: existing.ownerUsername,
      stackId: null,
      createdAt: existing.createdAt,
      updatedAt: existing.updatedAt,
      lastUserMessageAt: existing.lastUserMessageAt,
      lastAssistantMessageAt: existing.lastAssistantMessageAt,
      title: existing.title,
      historyPrimed: preservedHistoryPrimed,
      lastUsage: preservedLastUsage,
      lastError: preservedLastError,
      attention: preservedAttention,
    });
  }

  async closeAgent(agentId: string): Promise<void> {
    const agent = this.requireAgent(agentId);
    this.logger.trace(
      {
        agentId,
        lifecycle: agent.lifecycle,
        activeForegroundTurnId: agent.activeForegroundTurnId,
        pendingPermissions: agent.pendingPermissions.size,
      },
      "closeAgent: start",
    );
    const closedAgent = this.prepareAgentForClosure(agent, "agent closed");
    await agent.session.close();
    this.timelineStore.delete(agentId);
    await this.persistSnapshot(closedAgent, {
      internal: agent.internal,
    });
    this.emitClosedAgent(closedAgent, { persist: false });
    // Flush the final runtime patch (status + attention) directly — the
    // debounced drain scheduled by emitState would find the agent gone from
    // this.agents and bail, leaving the cloud tab row stuck on "running".
    const pendingFanOut = this.sessionFanOutTimers.get(agentId);
    if (pendingFanOut) {
      clearTimeout(pendingFanOut);
      this.sessionFanOutTimers.delete(agentId);
    }
    void this.drainSessionFanOut(agentId, closedAgent).catch((err) => {
      this.logger.debug({ err, agentId }, "closeAgent: final session fan-out failed");
    });
    this.logger.trace({ agentId }, "closeAgent: completed");
  }

  async archiveAgent(agentId: string): Promise<{ archivedAt: string }> {
    const agent = this.requireAgent(agentId);
    if (!this.registry) {
      throw new Error("Agent storage is not configured");
    }

    await this.registry.applySnapshot(agent, {
      internal: agent.internal,
    });
    const stored = await this.registry.get(agentId);
    if (!stored) {
      throw new Error(`Agent ${agentId} not found in storage after snapshot`);
    }

    // Capture sharees + owner BEFORE we mutate `stored` so the
    // fan-out callback knows who to clean up.
    const shareesAtArchive: ReadonlyArray<{ userId: string; orgId: string }> = (
      stored.sessionShares ?? []
    ).map((s) => ({ userId: s.userId, orgId: s.orgId }));
    const agentOwnerUserId = stored.ownerUserId ?? null;
    const agentCwd = stored.cwd;

    const archivedAt = new Date().toISOString();
    const normalizedStatus =
      stored.lastStatus === "running" || stored.lastStatus === "initializing"
        ? "idle"
        : stored.lastStatus;

    await this.registry.upsert({
      ...stored,
      archivedAt,
      updatedAt: archivedAt,
      lastStatus: normalizedStatus,
      requiresAttention: false,
      attentionReason: null,
      attentionTimestamp: null,
    });
    // Mirror archivedAt onto the live in-memory agent so the broadcast below
    // (and the closeAgent → emitClosedAgent broadcast that follows) carries it
    // through `toAgentPayload` to every connected session — including paired
    // mobile clients that don't run the originating archive mutation.
    agent.archivedAt = archivedAt;
    this.notifyAgentState(agentId);
    await this.closeAgent(agentId);

    // Fan out the archive across every user with a tab pointing at this
    // agent. Owner's tab → ARCHIVED (their historical record), every
    // sharee's tab → DELETED (no archive entry for sharees). Fire-and-
    // forget — failures don't roll back the primary archive.
    if (this.onAgentArchived && agentOwnerUserId) {
      const ownerUserId = agentOwnerUserId;
      void this.onAgentArchived({
        agentId,
        agentCwd,
        agentOwnerUserId: ownerUserId,
        sharees: shareesAtArchive,
      }).catch((err) => {
        this.logger.warn({ err, agentId }, "onAgentArchived hook failed");
      });
    }

    return { archivedAt };
  }

  /**
   * Fire the unarchive fan-out for a just-revived agent. Called by
   * `unarchiveAgentState` (mcp-shared) after `archivedAt` flips to null.
   * Fire-and-forget — failures never roll back the revive.
   */
  notifyAgentUnarchived(record: {
    id: string;
    cwd: string;
    ownerUserId?: string | null;
    title?: string | null;
    provider: string;
    sessionShares?: Array<{ userId: string; orgId: string; grantedAt: string }>;
  }): void {
    if (!this.onAgentUnarchived || !record.ownerUserId) return;
    void this.onAgentUnarchived({
      agentId: record.id,
      agentCwd: record.cwd,
      agentOwnerUserId: record.ownerUserId,
      agentTitle: record.title ?? null,
      provider: record.provider as AgentProvider,
      sharees: (record.sessionShares ?? []).map((s) => ({ userId: s.userId, orgId: s.orgId })),
    }).catch((err) => {
      this.logger.warn({ err, agentId: record.id }, "onAgentUnarchived hook failed");
    });
  }

  async setAgentMode(agentId: string, modeId: string): Promise<void> {
    const agent = this.requireSessionAgent(agentId);
    await agent.session.setMode(modeId);
    agent.config.modeId = modeId;
    agent.currentModeId = modeId;
    // Update runtimeInfo to reflect the new mode
    if (agent.runtimeInfo) {
      agent.runtimeInfo = { ...agent.runtimeInfo, modeId };
    }
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async setAgentModel(agentId: string, modelId: string | null): Promise<void> {
    const agent = this.requireSessionAgent(agentId);
    const normalizedModelId =
      typeof modelId === "string" && modelId.trim().length > 0 ? modelId : null;

    if (agent.session.setModel) {
      await agent.session.setModel(normalizedModelId);
    }

    agent.config.model = normalizedModelId ?? undefined;
    if (agent.runtimeInfo) {
      agent.runtimeInfo = { ...agent.runtimeInfo, model: normalizedModelId };
    }
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async setAgentThinkingOption(agentId: string, thinkingOptionId: string | null): Promise<void> {
    const agent = this.requireSessionAgent(agentId);
    const normalizedThinkingOptionId =
      typeof thinkingOptionId === "string" && thinkingOptionId.trim().length > 0
        ? thinkingOptionId
        : null;

    if (agent.session.setThinkingOption) {
      await agent.session.setThinkingOption(normalizedThinkingOptionId);
    }

    agent.config.thinkingOptionId = normalizedThinkingOptionId ?? undefined;
    if (agent.runtimeInfo) {
      agent.runtimeInfo = {
        ...agent.runtimeInfo,
        thinkingOptionId: normalizedThinkingOptionId,
      };
    }
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async listAgentMcpServers(agentId: string): Promise<Array<{ name: string; status: string }>> {
    const agent = this.requireAgent(agentId);
    if (!agent.session.listMcpServers) {
      return [];
    }
    return agent.session.listMcpServers();
  }

  async setAgentMcpServer(agentId: string, serverName: string, enabled: boolean): Promise<void> {
    const agent = this.requireAgent(agentId);
    if (enabled) {
      if (!agent.session.connectMcpServer) {
        throw new Error("Agent provider does not support MCP server management");
      }
      await agent.session.connectMcpServer(serverName);
      const current = agent.config.allowedMcpServers ?? [];
      if (!current.includes(serverName)) {
        agent.config.allowedMcpServers = [...current, serverName];
      }
    } else {
      if (!agent.session.disconnectMcpServer) {
        throw new Error("Agent provider does not support MCP server management");
      }
      await agent.session.disconnectMcpServer(serverName);
      agent.config.allowedMcpServers = (agent.config.allowedMcpServers ?? []).filter(
        (n) => n !== serverName,
      );
    }
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async setAgentFeature(agentId: string, featureId: string, value: unknown): Promise<void> {
    const agent = this.requireAgent(agentId);

    if (!agent.session.setFeature) {
      throw new Error("Agent session does not support setting features");
    }

    await agent.session.setFeature(featureId, value);
    agent.config.featureValues = { ...agent.config.featureValues, [featureId]: value };
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async setTitle(agentId: string, title: string): Promise<void> {
    const agent = this.requireAgent(agentId);
    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
      return;
    }
    if (
      this.agentsAwaitingInitialSnapshotPersist.has(agent.id) &&
      this.registry &&
      (await this.registry.get(agent.id)) === null
    ) {
      return;
    }
    this.touchUpdatedAt(agent);
    // Bump in-memory cache so drainSessionFanOut (which can't await a
    // registry read) reflects the new title on the next emitState.
    agent.title = normalizedTitle;
    await this.persistSnapshot(agent, { title: normalizedTitle });
    this.emitState(agent, { persist: false });
  }

  async setLabels(agentId: string, labels: Record<string, string>): Promise<void> {
    const agent = this.requireAgent(agentId);
    agent.labels = { ...agent.labels, ...labels };
    this.touchUpdatedAt(agent);
    await this.persistSnapshot(agent);
    this.emitState(agent, { persist: false });
  }

  async shareSession(
    agentId: string,
    userId: string,
    orgId: string,
  ): Promise<Array<{ userId: string; orgId: string; grantedAt: string }>> {
    const registry = this.requireRegistry();
    const liveAgent = this.agents.get(agentId);
    const existing = liveAgent ? null : await registry.get(agentId);
    if (!liveAgent && !existing) {
      throw new Error(`Agent not found: ${agentId}`);
    }

    const grantedAt = new Date().toISOString();
    // Track whether this call newly granted access in-memory — useful
    // for logging only. The auth-server hook fires regardless, because
    // the daemon's `sessionShares` array and the auth-server's `tabs`
    // row can drift (witnessed in prod: archived agents carry stale
    // share entries with no corresponding tab row). `createSharedSessionTab`
    // is idempotent on the server side, so re-firing is safe and heals
    // any drift on the next click.
    let isNewGrant = false;

    let resultShares: Array<{ userId: string; orgId: string; grantedAt: string }>;
    let agentCwd: string;
    let agentTitle: string | null;
    let agentOwnerUserId: string | null;
    let agentArchived: boolean;

    if (liveAgent) {
      const shares = liveAgent.sessionShares ?? [];
      const alreadyGranted = shares.some((s) => s.userId === userId && s.orgId === orgId);
      if (!alreadyGranted) {
        shares.push({ userId, orgId, grantedAt });
        liveAgent.sessionShares = shares;
        isNewGrant = true;
      }
      // Persist via direct upsert so applySnapshot's preserve guard picks it up.
      // `title` only lives on the persisted record (not on the in-memory
      // `LiveManagedAgent` union), so we read it from the stored record we
      // already fetched here.
      const storedRecord = await registry.get(agentId);
      if (storedRecord) {
        await registry.upsert({ ...storedRecord, sessionShares: liveAgent.sessionShares });
      }
      this.emitState(liveAgent, { persist: false });
      resultShares = liveAgent.sessionShares ?? [];
      agentCwd = liveAgent.cwd;
      agentTitle = storedRecord?.title ?? null;
      agentOwnerUserId = liveAgent.ownerUserId ?? null;
      agentArchived = Boolean(liveAgent.archivedAt);
    } else {
      const currentShares = existing!.sessionShares ?? [];
      const alreadyGranted = currentShares.some((s) => s.userId === userId && s.orgId === orgId);
      if (!alreadyGranted) {
        currentShares.push({ userId, orgId, grantedAt });
        await registry.upsert({ ...existing!, sessionShares: currentShares });
        isNewGrant = true;
      }
      resultShares = currentShares;
      agentCwd = existing!.cwd;
      agentTitle = existing!.title ?? null;
      agentOwnerUserId = existing!.ownerUserId ?? null;
      agentArchived = Boolean(existing!.archivedAt);
    }

    // Fire-and-forget auth-server tab creation. Always fire when the
    // hook + ownerUserId are present — see the `isNewGrant` comment
    // above. The auth-server's `/daemon/sync/tabs` is idempotent;
    // re-firing on a duplicate click costs one POST but heals drift.
    if (this.onShareCreated && agentOwnerUserId) {
      this.logger.info(
        { agentId, recipientUserId: userId, orgId, isNewGrant },
        "shareSession: firing onShareCreated",
      );
      const ownerUserId = agentOwnerUserId;
      void this.onShareCreated({
        agentId,
        agentCwd,
        agentTitle,
        agentOwnerUserId: ownerUserId,
        recipientUserId: userId,
        orgId,
        agentArchived,
      }).catch((err) => {
        this.logger.warn(
          { err, agentId, recipientUserId: userId, orgId },
          "onShareCreated hook failed",
        );
      });
    }

    return resultShares;
  }

  async unshareSession(
    agentId: string,
    userId: string,
    orgId: string,
  ): Promise<Array<{ userId: string; orgId: string; grantedAt: string }>> {
    const registry = this.requireRegistry();
    const liveAgent = this.agents.get(agentId);
    const existing = liveAgent ? null : await registry.get(agentId);
    if (!liveAgent && !existing) {
      throw new Error(`Agent not found: ${agentId}`);
    }

    // Detect whether THIS call actually removed a grant. If the share
    // didn't exist we skip the fan-out callback (no point deleting an
    // already-absent tab).
    let didRevoke = false;
    let agentOwnerUserId: string | null = null;
    let agentCwd: string;
    let resultShares: Array<{ userId: string; orgId: string; grantedAt: string }>;

    if (liveAgent) {
      const before = liveAgent.sessionShares ?? [];
      const after = before.filter((s) => !(s.userId === userId && s.orgId === orgId));
      didRevoke = after.length < before.length;
      liveAgent.sessionShares = after;
      const storedRecord = await registry.get(agentId);
      if (storedRecord) {
        await registry.upsert({ ...storedRecord, sessionShares: after });
      }
      this.emitState(liveAgent, { persist: false });
      agentOwnerUserId = liveAgent.ownerUserId ?? null;
      agentCwd = liveAgent.cwd;
      resultShares = after;
    } else {
      const before = existing!.sessionShares ?? [];
      const after = before.filter((s) => !(s.userId === userId && s.orgId === orgId));
      didRevoke = after.length < before.length;
      await registry.upsert({ ...existing!, sessionShares: after });
      agentOwnerUserId = existing!.ownerUserId ?? null;
      agentCwd = existing!.cwd;
      resultShares = after;
    }

    if (didRevoke && this.onShareRevoked && agentOwnerUserId) {
      const ownerUserId = agentOwnerUserId;
      void this.onShareRevoked({
        agentId,
        agentCwd,
        agentOwnerUserId: ownerUserId,
        revokedUserId: userId,
      }).catch((err) => {
        this.logger.warn({ err, agentId, revokedUserId: userId }, "onShareRevoked hook failed");
      });
    }

    return resultShares;
  }

  /**
   * @deprecated Phase 7 — no-op. Stack membership now lives on
   * RemoteTab.stackId (auth-server). Returns 0 (no agents moved).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async unsetStackForAgents(_options: {
    ownerUserId: string;
    workspaceId: string;
    fromStackId: string;
  }): Promise<number> {
    return 0;
  }

  notifyAgentState(agentId: string): void {
    const agent = this.agents.get(agentId);
    if (!agent || agent.internal) {
      return;
    }
    this.touchUpdatedAt(agent);
    this.emitState(agent);
  }

  async clearAgentAttention(agentId: string): Promise<void> {
    const agent = this.requireAgent(agentId);
    if (agent.attention.requiresAttention) {
      agent.attention = { requiresAttention: false };
      await this.persistSnapshot(agent);
      this.emitState(agent, { persist: false });
    }
  }

  async archiveSnapshot(agentId: string, archivedAt: string): Promise<StoredAgentRecord> {
    const registry = this.requireRegistry();
    const liveAgent = this.agents.get(agentId);
    if (liveAgent) {
      await this.persistSnapshot(liveAgent, {
        internal: liveAgent.internal,
      });
    }

    const record = await registry.get(agentId);
    if (!record) {
      throw new Error(`Agent not found: ${agentId}`);
    }

    const normalizedStatus =
      record.lastStatus === "running" || record.lastStatus === "initializing"
        ? "idle"
        : record.lastStatus;

    const nextRecord: StoredAgentRecord = {
      ...record,
      archivedAt,
      lastStatus: normalizedStatus,
      requiresAttention: false,
      attentionReason: null,
      attentionTimestamp: null,
    };
    await registry.upsert(nextRecord);
    return nextRecord;
  }

  async unarchiveSnapshot(agentId: string): Promise<boolean> {
    const registry = this.requireRegistry();
    const record = await registry.get(agentId);
    if (!record || !record.archivedAt) {
      return false;
    }

    await registry.upsert({
      ...record,
      archivedAt: null,
    });

    if (this.agents.has(agentId)) {
      this.notifyAgentState(agentId);
    }
    return true;
  }

  async unarchiveSnapshotByHandle(handle: AgentPersistenceHandle): Promise<void> {
    const registry = this.requireRegistry();
    const records = await registry.list();
    const matched = records.find(
      (record) =>
        record.persistence?.provider === handle.provider &&
        record.persistence?.sessionId === handle.sessionId,
    );
    if (!matched) {
      return;
    }

    await this.unarchiveSnapshot(matched.id);
  }

  async updateAgentMetadata(
    agentId: string,
    updates: {
      title?: string;
      labels?: Record<string, string>;
      internal?: boolean;
      stackId?: string | null;
    },
  ): Promise<void> {
    // Use the live map entry directly — `liveAgent.internal = ...` below must
    // persist to the canonical `this.agents` entry; a snapshot clone would
    // lose the write.
    const liveAgent = this.agents.get(agentId);
    if (liveAgent) {
      if (updates.title) {
        await this.setTitle(agentId, updates.title);
      }
      if (updates.labels) {
        await this.setLabels(agentId, updates.labels);
      }
      if (updates.internal !== undefined) {
        const wasInternal = liveAgent.internal === true;
        liveAgent.internal = updates.internal;
        await this.persistSnapshot(liveAgent, { internal: updates.internal });
        this.dispatch({ type: "agent_state", agent: { ...liveAgent } });
        // Kick the runtime fan-out so the auth-server projection picks up
        // the visibility flip without waiting for the next agent event.
        this.markSessionDirty(agentId);
        if (wasInternal && updates.internal === false && this.onAgentPromoted) {
          const ownerUserId = liveAgent.ownerUserId;
          if (ownerUserId) {
            // Promote = internal → visible. Materialise a tab on the
            // auth-server (internal agents never got one). Fire-and-forget.
            void this.onAgentPromoted({
              agentId,
              agentCwd: liveAgent.cwd,
              agentOwnerUserId: ownerUserId,
              agentTitle: liveAgent.title ?? null,
              provider: liveAgent.provider,
            })
              .then(() => {
                // The tab row now exists on the auth-server — kick the
                // runtime fan-out again so it gets status/attention right
                // away instead of waiting for the next agent event. (The
                // markSessionDirty above may have drained before the tab
                // existed and 404ed.)
                this.markSessionDirty(agentId);
              })
              .catch((err) => {
                this.logger.warn({ err, agentId }, "onAgentPromoted hook failed");
              });
          }
        }
      }
      // stackId on agent-manager is deprecated (Phase 7); ignore it.
      return;
    }

    const registry = this.requireRegistry();
    const existing = await registry.get(agentId);
    if (!existing) {
      throw new Error(`Agent not found: ${agentId}`);
    }

    await registry.upsert({
      ...existing,
      ...(updates.title ? { title: updates.title } : {}),
      ...(updates.labels ? { labels: { ...existing.labels, ...updates.labels } } : {}),
    });
  }

  async runAgent(
    agentId: string,
    prompt: AgentPromptInput,
    options?: AgentRunOptions,
  ): Promise<AgentRunResult> {
    const events = this.streamAgent(agentId, prompt, options);
    const timeline: AgentTimelineItem[] = [];
    let finalText = "";
    let usage: AgentUsage | undefined;
    let canceled = false;

    for await (const event of events) {
      if (event.type === "timeline") {
        timeline.push(event.item);
      } else if (event.type === "turn_completed") {
        usage = event.usage;
      } else if (event.type === "turn_failed") {
        throw new Error(this.formatTurnFailedMessage(event));
      } else if (event.type === "turn_canceled") {
        canceled = true;
      }
    }

    finalText = this.getLastAssistantMessageFromTimeline(timeline) ?? "";

    const agent = this.requireAgent(agentId);
    const sessionId = agent.persistence?.sessionId;
    if (!sessionId) {
      throw new Error(`Agent ${agentId} has no persistence.sessionId after run completed`);
    }
    return {
      sessionId,
      finalText,
      usage,
      timeline,
      canceled,
    };
  }

  recordUserMessage(
    agentId: string,
    text: string,
    options?: {
      messageId?: string;
      emitState?: boolean;
      // Bytes-stripped attachment metadata. Persisted on the timeline row so
      // a reload can redraw the chip from local AttachmentStore lookups
      // (web; the desktop/native cross-device case still falls back to the
      // existing thumbnail placeholder, but the chip itself appears).
      images?: import("./agent-sdk-types.js").UserMessageImageEcho[];
      files?: import("./agent-sdk-types.js").UserMessageFileEcho[];
      /**
       * True when `text` was synthesized by the daemon for non-empty /
       * cache_control compliance (e.g. "Sent 1 image"). The chat bubble
       * hides the text when this is set; the LLM still sees it.
       */
      textIsSynthesized?: boolean;
    },
  ): void {
    const agent = this.requireAgent(agentId);
    const normalizedMessageId = normalizeMessageId(options?.messageId);
    const item: AgentTimelineItem = {
      type: "user_message",
      text,
      messageId: normalizedMessageId,
      ...(options?.images && options.images.length > 0 ? { images: options.images } : {}),
      ...(options?.files && options.files.length > 0 ? { files: options.files } : {}),
      ...(options?.textIsSynthesized ? { textIsSynthesized: true } : {}),
    };
    const updatedAt = this.touchUpdatedAt(agent);
    agent.lastUserMessageAt = updatedAt;
    const row = this.recordTimeline(agentId, item);
    this.dispatchStream(
      agentId,
      {
        type: "timeline",
        item,
        provider: agent.provider,
      },
      {
        seq: row.seq,
        epoch: this.timelineStore.getEpoch(agentId),
      },
    );
    if (options?.emitState !== false) {
      this.emitState(agent);
    }
  }

  async appendTimelineItem(agentId: string, item: AgentTimelineItem): Promise<void> {
    const agent = this.requireAgent(agentId);
    this.touchUpdatedAt(agent);
    const row = this.recordTimeline(agentId, item);
    this.dispatchStream(
      agentId,
      {
        type: "timeline",
        item,
        provider: agent.provider,
      },
      {
        seq: row.seq,
        epoch: this.timelineStore.getEpoch(agentId),
      },
    );
    await this.persistSnapshot(agent);
  }

  async emitLiveTimelineItem(agentId: string, item: AgentTimelineItem): Promise<void> {
    const agent = this.requireAgent(agentId);
    this.touchUpdatedAt(agent);
    this.dispatchStream(agentId, {
      type: "timeline",
      item,
      provider: agent.provider,
    });
  }

  streamAgent(
    agentId: string,
    prompt: AgentPromptInput,
    options?: AgentRunOptions,
  ): AsyncGenerator<AgentStreamEvent> {
    const existingAgent = this.requireSessionAgent(agentId);
    this.logger.trace(
      {
        agentId,
        lifecycle: existingAgent.lifecycle,
        activeForegroundTurnId: existingAgent.activeForegroundTurnId,
        hasPendingForegroundRun: this.hasPendingForegroundRun(agentId),
        promptType: typeof prompt === "string" ? "string" : "structured",
        hasRunOptions: Boolean(options),
      },
      "streamAgent: requested",
    );
    if (existingAgent.activeForegroundTurnId || this.hasPendingForegroundRun(agentId)) {
      this.logger.trace(
        {
          agentId,
          lifecycle: existingAgent.lifecycle,
          hasPendingForegroundRun: this.hasPendingForegroundRun(agentId),
        },
        "streamAgent: rejected because a foreground run is already in flight",
      );
      throw new Error(`Agent ${agentId} already has an active run`);
    }

    const agent = existingAgent as ActiveManagedAgent;
    agent.pendingReplacement = false;
    agent.lastError = undefined;

    const self = this;
    const pendingRun = self.createPendingForegroundRun();
    self.pendingForegroundRuns.set(agentId, pendingRun);

    const streamForwarder = (async function* streamForwarder() {
      let turnId: string;
      let waiter: ForegroundTurnWaiter | null = null;
      try {
        const result = await agent.session.startTurn(prompt, options);
        turnId = result.turnId;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Failed to start turn";
        self.handleStreamEvent(agent, {
          type: "turn_failed",
          provider: agent.provider,
          error: errorMsg,
        });
        self.finalizeForegroundTurn(agent);
        throw error;
      }

      pendingRun.started = true;
      agent.activeForegroundTurnId = turnId;
      agent.lifecycle = "running";
      self.touchUpdatedAt(agent);
      self.emitState(agent);
      self.logger.trace(
        {
          agentId,
          lifecycle: agent.lifecycle,
          activeForegroundTurnId: agent.activeForegroundTurnId,
        },
        "streamAgent: started",
      );

      // Create a pushable queue for this foreground turn
      const queue: AgentStreamEvent[] = [];
      let queueResolve: (() => void) | null = null;
      let done = false;
      let resolveSettled!: () => void;
      const settledPromise = new Promise<void>((resolve) => {
        resolveSettled = resolve;
      });

      waiter = {
        turnId,
        settled: false,
        settledPromise,
        resolveSettled,
        callback: (event: AgentStreamEvent) => {
          queue.push(event);
          if (queueResolve) {
            queueResolve();
            queueResolve = null;
          }
        },
      };
      agent.foregroundTurnWaiters.add(waiter);

      try {
        while (!done) {
          while (queue.length > 0) {
            const event = queue.shift()!;
            yield event;
            if (isTurnTerminalEvent(event)) {
              done = true;
              break;
            }
          }
          if (!done && queue.length === 0) {
            if (waiter.settled) {
              break;
            }
            await new Promise<void>((resolve) => {
              queueResolve = resolve;
            });
          }
        }
      } finally {
        if (waiter) {
          agent.foregroundTurnWaiters.delete(waiter);
          self.settleForegroundTurnWaiter(waiter);
        }
        self.settlePendingForegroundRun(agentId, pendingRun.token);
        if (!agent.activeForegroundTurnId) {
          await self.refreshRuntimeInfo(agent);
        }
      }
    })();

    return streamForwarder;
  }

  private finalizeForegroundTurn(agent: ActiveManagedAgent): void {
    const mutableAgent = agent as ActiveManagedAgent;
    mutableAgent.activeForegroundTurnId = null;
    const terminalError = mutableAgent.lastError;
    const shouldHoldBusyForReplacement = mutableAgent.pendingReplacement && !terminalError;
    mutableAgent.lifecycle = shouldHoldBusyForReplacement
      ? "running"
      : terminalError
        ? "error"
        : "idle";
    const persistenceHandle =
      mutableAgent.session.describePersistence() ??
      (mutableAgent.runtimeInfo?.sessionId
        ? { provider: mutableAgent.provider, sessionId: mutableAgent.runtimeInfo.sessionId }
        : null);
    if (persistenceHandle) {
      mutableAgent.persistence = attachPersistenceCwd(persistenceHandle, mutableAgent.cwd);
    }
    this.logger.trace(
      {
        agentId: agent.id,
        lifecycle: mutableAgent.lifecycle,
        terminalError,
        pendingReplacement: mutableAgent.pendingReplacement,
      },
      "finalizeForegroundTurn: applying terminal state",
    );
    if (!shouldHoldBusyForReplacement) {
      this.touchUpdatedAt(mutableAgent);
      this.emitState(mutableAgent);
    }
  }

  replaceAgentRun(
    agentId: string,
    prompt: AgentPromptInput,
    options?: AgentRunOptions,
  ): AsyncGenerator<AgentStreamEvent> {
    const snapshot = this.requireAgent(agentId);
    if (
      snapshot.lifecycle !== "running" &&
      !snapshot.activeForegroundTurnId &&
      !this.hasPendingForegroundRun(agentId)
    ) {
      return this.streamAgent(agentId, prompt, options);
    }

    const agent = this.requireSessionAgent(agentId);
    agent.pendingReplacement = true;

    const self = this;
    return (async function* replaceRunForwarder() {
      try {
        await self.cancelAgentRun(agentId);
        const nextRun = self.streamAgent(agentId, prompt, options);
        for await (const event of nextRun) {
          yield event;
        }
      } catch (error) {
        const latest = self.agents.get(agentId);
        if (latest) {
          const latestActive = latest as ActiveManagedAgent;
          latestActive.pendingReplacement = false;
          if (!latestActive.activeForegroundTurnId && latestActive.lifecycle === "running") {
            (latestActive as ActiveManagedAgent).lifecycle = "idle";
            self.touchUpdatedAt(latestActive);
            self.emitState(latestActive);
          }
        }
        throw error;
      }
    })();
  }

  async waitForAgentRunStart(agentId: string, options?: WaitForAgentStartOptions): Promise<void> {
    const snapshot = this.getAgentSnapshot(agentId);
    if (!snapshot) {
      throw new Error(`Agent ${agentId} not found`);
    }

    const pendingRun = this.getPendingForegroundRun(agentId);
    if ((snapshot.lifecycle === "running" || pendingRun?.started) && !snapshot.pendingReplacement) {
      return;
    }

    if (!snapshot.activeForegroundTurnId && !pendingRun && !snapshot.pendingReplacement) {
      throw new Error(`Agent ${agentId} has no pending run`);
    }

    if (options?.signal?.aborted) {
      throw createAbortError(options.signal, "wait_for_agent_start aborted");
    }

    await new Promise<void>((resolve, reject) => {
      if (options?.signal?.aborted) {
        reject(createAbortError(options.signal, "wait_for_agent_start aborted"));
        return;
      }

      let unsubscribe: (() => void) | null = null;
      let abortHandler: (() => void) | null = null;

      const cleanup = () => {
        if (unsubscribe) {
          try {
            unsubscribe();
          } catch {
            // ignore cleanup errors
          }
          unsubscribe = null;
        }
        if (abortHandler && options?.signal) {
          try {
            options.signal.removeEventListener("abort", abortHandler);
          } catch {
            // ignore cleanup errors
          }
          abortHandler = null;
        }
      };

      const finishOk = () => {
        cleanup();
        resolve();
      };

      const finishErr = (error: unknown) => {
        cleanup();
        reject(error);
      };

      if (options?.signal) {
        abortHandler = () =>
          finishErr(createAbortError(options.signal!, "wait_for_agent_start aborted"));
        options.signal.addEventListener("abort", abortHandler, { once: true });
      }

      const checkCurrentState = () => {
        const current = this.getAgentSnapshot(agentId);
        if (!current) {
          finishErr(new Error(`Agent ${agentId} not found`));
          return true;
        }

        const currentPendingRun = this.getPendingForegroundRun(agentId);
        if (
          (current.lifecycle === "running" || currentPendingRun?.started) &&
          !current.pendingReplacement
        ) {
          finishOk();
          return true;
        }

        if (current.lifecycle === "error" && !currentPendingRun?.started) {
          finishErr(new Error(current.lastError ?? `Agent ${agentId} failed to start`));
          return true;
        }

        if (!currentPendingRun && !current.activeForegroundTurnId && !current.pendingReplacement) {
          finishErr(new Error(`Agent ${agentId} run finished before starting`));
          return true;
        }

        return false;
      };

      unsubscribe = this.subscribe(
        (event) => {
          if (event.type !== "agent_state" || event.agent.id !== agentId) {
            return;
          }
          checkCurrentState();
        },
        { agentId, replayState: false },
      );

      checkCurrentState();
    });
  }

  async respondToPermission(
    agentId: string,
    requestId: string,
    response: AgentPermissionResponse,
  ): Promise<AgentPermissionResult | void> {
    const agent = this.requireAgent(agentId);
    agent.inFlightPermissionResponses.add(requestId);

    try {
      const result = await agent.session.respondToPermission(requestId, response);
      agent.pendingPermissions.delete(requestId);

      try {
        await this.refreshSessionState(agent);
      } catch {
        // Ignore refresh errors - state sync after permission approval is best effort.
      }

      this.touchUpdatedAt(agent);
      await this.persistSnapshot(agent);
      this.emitState(agent);

      const bufferedResolution = agent.bufferedPermissionResolutions.get(requestId);
      if (bufferedResolution) {
        agent.bufferedPermissionResolutions.delete(requestId);
        this.dispatchStream(agent.id, bufferedResolution);
      }

      return result;
    } finally {
      agent.inFlightPermissionResponses.delete(requestId);
      agent.bufferedPermissionResolutions.delete(requestId);
    }
  }

  async cancelAgentRun(agentId: string): Promise<boolean> {
    const agent = this.requireSessionAgent(agentId);
    const pendingRun = this.getPendingForegroundRun(agentId);
    const foregroundTurnId = agent.activeForegroundTurnId;
    const hasForegroundTurn = Boolean(foregroundTurnId);
    const isAutonomousRunning = agent.lifecycle === "running" && !hasForegroundTurn && !pendingRun;

    if (!hasForegroundTurn && !isAutonomousRunning && !pendingRun) {
      return false;
    }

    try {
      await agent.session.interrupt();
    } catch (error) {
      this.logger.error({ err: error, agentId }, "Failed to interrupt session");
    }

    // The interrupt will produce a turn_canceled/turn_failed event via subscribe(),
    // which flows through the session event dispatcher and settles the foreground turn waiter.
    // Wait briefly for the event to propagate if there's an active foreground turn.
    if (foregroundTurnId) {
      const waiter = Array.from(agent.foregroundTurnWaiters).find(
        (candidate) => candidate.turnId === foregroundTurnId,
      );
      const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000));
      if (waiter) {
        await Promise.race([waiter.settledPromise, timeout]);
      } else if (agent.activeForegroundTurnId === foregroundTurnId) {
        await Promise.race([
          new Promise<void>((resolve) => {
            const unsubscribe = this.subscribe(
              (event) => {
                if (
                  event.type === "agent_state" &&
                  event.agent.id === agentId &&
                  !event.agent.activeForegroundTurnId
                ) {
                  unsubscribe();
                  resolve();
                }
              },
              { agentId, replayState: false },
            );
          }),
          timeout,
        ]);
      }
      // The waiter settling wakes up the streamForwarder generator, but its
      // finally block (which deletes the pendingForegroundRun) runs asynchronously.
      // Wait for the pending run to be fully cleaned up so the next streamAgent
      // call doesn't see a stale entry and reject with "already has an active run".
      if (pendingRun && !pendingRun.settled) {
        await Promise.race([pendingRun.settledPromise, timeout]);
      }
    } else if (pendingRun) {
      const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000));
      await Promise.race([pendingRun.settledPromise, timeout]);
    }

    // If the foreground turn is still stuck after the timeout, force-dispatch a
    // synthetic turn_canceled so the normal event pipeline cleans up
    // activeForegroundTurnId, settles waiters, and unblocks the streamForwarder.
    if (foregroundTurnId && agent.activeForegroundTurnId === foregroundTurnId) {
      this.logger.warn(
        { agentId, foregroundTurnId },
        "cancelAgentRun: foreground turn still active after timeout, force-canceling",
      );
      this.dispatchSessionEvent(agent, {
        type: "turn_canceled",
        provider: agent.provider,
        reason: "interrupted",
        turnId: foregroundTurnId,
      });
      // The synthetic event unblocks the streamForwarder generator, whose finally
      // block settles the pending foreground run asynchronously. Wait for it.
      const staleRun = this.getPendingForegroundRun(agentId);
      if (staleRun && !staleRun.settled) {
        await staleRun.settledPromise;
      }
    }

    // Clear any pending permissions that weren't cleaned up by handleStreamEvent.
    if (agent.pendingPermissions.size > 0) {
      for (const [requestId] of agent.pendingPermissions) {
        this.dispatchStream(agent.id, {
          type: "permission_resolved",
          provider: agent.provider,
          requestId,
          resolution: { behavior: "deny", message: "Interrupted" },
        });
      }
      agent.pendingPermissions.clear();
      this.touchUpdatedAt(agent);
      this.emitState(agent);
    }

    return true;
  }

  getPendingPermissions(agentId: string): AgentPermissionRequest[] {
    const agent = this.requireSessionAgent(agentId);
    return Array.from(agent.pendingPermissions.values());
  }

  private peekPendingPermission(agent: ManagedAgent): AgentPermissionRequest | null {
    const iterator = agent.pendingPermissions.values().next();
    return iterator.done ? null : iterator.value;
  }

  /**
   * Hydrates the timeline from provider history if the agent's durable
   * timeline is empty (e.g., imported agents that have provider history
   * on disk but no persisted timeline rows). No-ops if already hydrated.
   */
  async hydrateTimelineFromProvider(agentId: string): Promise<void> {
    const agent = this.requireSessionAgent(agentId);
    await this.hydrateTimelineFromLegacyProviderHistory(agent);
  }

  async deleteCommittedTimeline(agentId: string): Promise<void> {
    if (!this.durableTimelineStore) {
      return;
    }
    await this.durableTimelineStore.deleteAgent(agentId);
  }

  async getLastAssistantMessage(agentId: string): Promise<string | null> {
    const agent = this.agents.get(agentId);
    if (!agent) {
      return null;
    }

    return await this.getLastAssistantMessageFromStores(agentId);
  }

  private getLastAssistantMessageFromTimeline(
    timeline: readonly AgentTimelineItem[],
  ): string | null {
    return this.getLastAssistantMessageSegmentFromTimeline(timeline)?.text ?? null;
  }

  private getLastAssistantMessageSegmentFromTimeline(
    timeline: readonly AgentTimelineItem[],
  ): { text: string; startsAtBeginning: boolean } | null {
    // Collect the last contiguous assistant messages (Claude streams chunks)
    const chunks: string[] = [];
    let startsAtBeginning = false;
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i];
      if (item.type !== "assistant_message") {
        if (chunks.length) {
          break;
        }
        continue;
      }
      chunks.push(item.text);
      startsAtBeginning = i === 0;
    }

    if (!chunks.length) {
      return null;
    }

    return {
      text: chunks.reverse().join(""),
      startsAtBeginning,
    };
  }

  private async getLastAssistantMessageFromStores(agentId: string): Promise<string | null> {
    const liveTimeline = this.timelineStore.getItems(agentId);
    const liveSegment = this.getLastAssistantMessageSegmentFromTimeline(liveTimeline);
    if (!this.durableTimelineStore) {
      return liveSegment?.text ?? null;
    }

    if (!liveSegment) {
      return await this.durableTimelineStore.getLastAssistantMessage(agentId);
    }

    if (!liveSegment.startsAtBeginning) {
      return liveSegment.text;
    }

    const lastDurableItem = await this.durableTimelineStore.getLastItem(agentId);
    if (lastDurableItem?.type !== "assistant_message") {
      return liveSegment.text;
    }

    const durableMessage = await this.durableTimelineStore.getLastAssistantMessage(agentId);
    return durableMessage ? `${durableMessage}${liveSegment.text}` : liveSegment.text;
  }

  private async getLastItemFromStores(agentId: string): Promise<AgentTimelineItem | null> {
    const lastLiveItem = this.timelineStore.getLastItem(agentId);
    if (lastLiveItem) {
      return lastLiveItem;
    }
    if (!this.durableTimelineStore) {
      return null;
    }
    return await this.durableTimelineStore.getLastItem(agentId);
  }

  private async hasCommittedUserMessageFromStores(
    agentId: string,
    options: { messageId: string; text: string },
  ): Promise<boolean> {
    if (this.timelineStore.hasCommittedUserMessage(agentId, options)) {
      return true;
    }
    if (!this.durableTimelineStore) {
      return false;
    }
    return await this.durableTimelineStore.hasCommittedUserMessage(agentId, options);
  }

  async waitForAgentEvent(
    agentId: string,
    options?: WaitForAgentOptions,
  ): Promise<WaitForAgentResult> {
    const snapshot = this.getAgentSnapshot(agentId);
    if (!snapshot) {
      throw new Error(`Agent ${agentId} not found`);
    }

    const pendingForegroundRun = this.getPendingForegroundRun(agentId);
    const hasForegroundTurn =
      Boolean(snapshot.activeForegroundTurnId) || Boolean(pendingForegroundRun);

    const immediatePermission = this.peekPendingPermission(snapshot);
    if (immediatePermission) {
      return {
        status: snapshot.lifecycle,
        permission: immediatePermission,
        lastMessage: await this.getLastAssistantMessage(agentId),
      };
    }

    const initialStatus = snapshot.lifecycle;
    const initialBusy = isAgentBusy(initialStatus) || hasForegroundTurn;
    const waitForActive = options?.waitForActive ?? false;
    if (!waitForActive && !initialBusy) {
      return {
        status: initialStatus,
        permission: null,
        lastMessage: await this.getLastAssistantMessage(agentId),
      };
    }
    if (waitForActive && !initialBusy && !hasForegroundTurn) {
      return {
        status: initialStatus,
        permission: null,
        lastMessage: await this.getLastAssistantMessage(agentId),
      };
    }

    if (options?.signal?.aborted) {
      throw createAbortError(options.signal, "wait_for_agent aborted");
    }

    return await new Promise<WaitForAgentResult>((resolve, reject) => {
      // Bug #1 Fix: Check abort signal AGAIN inside Promise constructor
      // to avoid race condition between pre-Promise check and abort listener registration
      if (options?.signal?.aborted) {
        reject(createAbortError(options.signal, "wait_for_agent aborted"));
        return;
      }

      let currentStatus: AgentLifecycleStatus = initialStatus;
      let hasStarted =
        isAgentBusy(initialStatus) ||
        Boolean(snapshot.activeForegroundTurnId) ||
        Boolean(pendingForegroundRun?.started);
      let terminalStatusOverride: AgentLifecycleStatus | null = null;
      let finished = false;

      // Bug #3 Fix: Declare unsubscribe and abortHandler upfront so cleanup can reference them
      let unsubscribe: (() => void) | null = null;
      let abortHandler: (() => void) | null = null;

      const cleanup = () => {
        // Clean up subscription
        if (unsubscribe) {
          try {
            unsubscribe();
          } catch {
            // ignore cleanup errors
          }
          unsubscribe = null;
        }

        // Clean up abort listener
        if (abortHandler && options?.signal) {
          try {
            options.signal.removeEventListener("abort", abortHandler);
          } catch {
            // ignore cleanup errors
          }
          abortHandler = null;
        }
      };

      const finish = (permission: AgentPermissionRequest | null) => {
        if (finished) {
          return;
        }
        finished = true;
        cleanup();
        void this.getLastAssistantMessage(agentId)
          .then((lastMessage) => {
            resolve({
              status: currentStatus,
              permission,
              lastMessage,
            });
          })
          .catch(reject);
      };

      // Bug #3 Fix: Set up abort handler BEFORE subscription
      // to ensure cleanup handlers exist before callback can fire
      if (options?.signal) {
        abortHandler = () => {
          cleanup();
          reject(createAbortError(options.signal, "wait_for_agent aborted"));
        };
        options.signal.addEventListener("abort", abortHandler, { once: true });
      }

      // Bug #3 Fix: Now subscribe with cleanup handlers already in place
      // This prevents race condition if callback fires synchronously with replayState: true
      unsubscribe = this.subscribe(
        (event) => {
          if (event.type === "agent_state") {
            currentStatus = event.agent.lifecycle;
            const pending = this.peekPendingPermission(event.agent);
            if (pending) {
              finish(pending);
              return;
            }
            if (isAgentBusy(event.agent.lifecycle)) {
              hasStarted = true;
              return;
            }
            if (!waitForActive || hasStarted) {
              if (terminalStatusOverride) {
                currentStatus = terminalStatusOverride;
              }
              finish(null);
            }
            return;
          }

          if (event.type === "agent_stream") {
            if (event.event.type === "permission_requested") {
              finish(event.event.request);
              return;
            }
            if (event.event.type === "turn_failed") {
              hasStarted = true;
              terminalStatusOverride = "error";
              return;
            }
            if (event.event.type === "turn_completed") {
              hasStarted = true;
            }
            if (event.event.type === "turn_canceled") {
              hasStarted = true;
            }
          }
        },
        { agentId, replayState: true },
      );
    });
  }

  private async registerSession(
    session: AgentSession,
    config: AgentSessionConfig,
    agentId: string,
    options?: {
      workspaceId?: string;
      createdAt?: Date;
      updatedAt?: Date;
      lastUserMessageAt?: Date | null;
      lastAssistantMessageAt?: Date | null;
      title?: string | null;
      labels?: Record<string, string>;
      timeline?: AgentTimelineItem[];
      timelineRows?: AgentTimelineRow[];
      timelineNextSeq?: number;
      historyPrimed?: boolean;
      lastUsage?: AgentUsage;
      lastError?: string;
      attention?: AttentionState;
      parentAgentId?: string;
      forkedFromMessageUuid?: string;
      /**
       * See `createAgent` — stamped at create-time. Optional only because
       * rehydrate paths load it from a record where the on-disk type still
       * allows `string | null` for legacy parsing (see T3 in the audit).
       * The migration runs at boot and rewrites every null-owner record, so
       * post-migration this is always present. If somehow null sneaks
       * through, the manager falls back to its own configured `ownerUserId`
       * and finally to the test-only placeholder.
       */
      ownerUserId?: string | null;
      /** Owner's display username for Claude profile-dir routing. */
      ownerUsername?: string | null;
      /**
       * Initial tab stack assignment. `null` = unstacked / Default. Used at
       * create-time (resolved from the user's active stack) and on
       * rehydrate (round-tripped from the stored record).
       */
      stackId?: string | null;
      /** Per-session share grants loaded from the stored record. */
      sessionShares?: Array<{ userId: string; orgId: string; grantedAt: string }>;
    },
  ): Promise<ManagedAgent> {
    const resolvedAgentId = validateAgentId(agentId, "registerSession");
    if (this.agents.has(resolvedAgentId)) {
      throw new Error(`Agent with id ${resolvedAgentId} already exists`);
    }
    const initialPersistedTitle = await this.resolveInitialPersistedTitle(resolvedAgentId, config);

    const now = new Date();
    const explicitTimelineSeed: SeedAgentTimelineOptions | null =
      options?.timeline?.length ||
      options?.timelineRows?.length ||
      options?.timelineNextSeq !== undefined
        ? {
            items: options?.timeline,
            rows: options?.timelineRows,
            nextSeq: options?.timelineNextSeq,
            timestamp: (options?.updatedAt ?? options?.createdAt ?? now).toISOString(),
          }
        : null;
    const shouldSeedFromDurable =
      !explicitTimelineSeed &&
      !this.timelineStore.has(resolvedAgentId) &&
      this.durableTimelineStore !== undefined;
    const durableTimelineSeed = shouldSeedFromDurable
      ? await this.loadCommittedTimelineSeed(resolvedAgentId, now)
      : null;
    const durableTimelineHasRows =
      durableTimelineSeed != null && (durableTimelineSeed.nextSeq ?? 1) > 1;
    const timelineSeed = explicitTimelineSeed ?? durableTimelineSeed;
    if (timelineSeed || !this.timelineStore.has(resolvedAgentId)) {
      this.timelineStore.initialize(
        resolvedAgentId,
        timelineSeed ?? { timestamp: now.toISOString() },
      );
    }
    if (options?.timelineRows?.length) {
      this.enqueueDurableTimelineBulkInsert(resolvedAgentId, options.timelineRows);
    }

    const managed = {
      id: resolvedAgentId,
      provider: config.provider,
      cwd: config.cwd,
      session,
      capabilities: session.capabilities,
      config,
      runtimeInfo: undefined,
      lifecycle: "initializing",
      createdAt: options?.createdAt ?? now,
      updatedAt: options?.updatedAt ?? now,
      availableModes: [],
      currentModeId: null,
      pendingPermissions: new Map<string, AgentPermissionRequest>(),
      bufferedPermissionResolutions: new Map(),
      inFlightPermissionResponses: new Set(),
      pendingReplacement: false,
      activeForegroundTurnId: null,
      foregroundTurnWaiters: new Set<ForegroundTurnWaiter>(),
      unsubscribeSession: null,
      persistence: attachPersistenceCwd(session.describePersistence(), config.cwd),
      historyPrimed: options?.historyPrimed ?? durableTimelineHasRows,
      lastUserMessageAt: options?.lastUserMessageAt ?? null,
      lastAssistantMessageAt: options?.lastAssistantMessageAt ?? null,
      title: options?.title ?? null,
      lastUsage: options?.lastUsage,
      lastError: options?.lastError,
      attention:
        options?.attention != null
          ? options.attention.requiresAttention
            ? {
                requiresAttention: true,
                attentionReason: options.attention.attentionReason,
                attentionTimestamp: new Date(options.attention.attentionTimestamp),
              }
            : { requiresAttention: false }
          : { requiresAttention: false },
      internal: config.internal ?? false,
      labels: options?.labels ?? {},
      ...(options?.parentAgentId ? { parentAgentId: options.parentAgentId } : {}),
      ...(options?.forkedFromMessageUuid
        ? { forkedFromMessageUuid: options.forkedFromMessageUuid }
        : {}),
      ownerUserId: options?.ownerUserId ?? this.ownerUserId ?? AGENT_MANAGER_TEST_OWNER_PLACEHOLDER,
      ownerUsername: options?.ownerUsername ?? null,
      stackId: null,
      ...(options?.sessionShares ? { sessionShares: options.sessionShares } : {}),
    } as ActiveManagedAgent;

    this.agents.set(resolvedAgentId, managed);
    // Initialize previousStatus to track transitions
    this.previousStatuses.set(resolvedAgentId, managed.lifecycle);
    // Sync the in-memory title cache with whatever we're about to persist
    // (the resolver may have pulled an auto-title from the registry / config
    // even when options.title was undefined). Lets drainSessionFanOut see
    // the title on the very first emitState without a registry round-trip.
    if (initialPersistedTitle && managed.title == null) {
      managed.title = initialPersistedTitle;
    }
    await this.refreshRuntimeInfo(managed);
    await this.persistSnapshot(managed, {
      workspaceId: options?.workspaceId,
      title: initialPersistedTitle,
    });
    this.emitState(managed, { persist: false });

    await this.refreshSessionState(managed);
    managed.lifecycle = "idle";
    await this.persistSnapshot(managed, { workspaceId: options?.workspaceId });
    this.emitState(managed, { persist: false });
    this.subscribeToSession(managed);
    return { ...managed };
  }

  private async loadCommittedTimelineSeed(
    agentId: string,
    now: Date,
  ): Promise<SeedAgentTimelineOptions> {
    if (!this.durableTimelineStore) {
      return { timestamp: now.toISOString() };
    }

    return {
      nextSeq: (await this.durableTimelineStore.getLatestCommittedSeq(agentId)) + 1,
      timestamp: now.toISOString(),
    };
  }

  private prepareAgentForClosure(
    agent: LiveManagedAgent,
    cancelReason: string,
  ): ManagedAgentClosed {
    this.agentStreamCoalescer.flushAndDiscard(agent.id);
    this.agents.delete(agent.id);
    this.previousStatuses.delete(agent.id);
    if (agent.unsubscribeSession) {
      agent.unsubscribeSession();
      agent.unsubscribeSession = null;
    }
    for (const waiter of agent.foregroundTurnWaiters) {
      waiter.callback({
        type: "turn_canceled",
        provider: agent.provider,
        reason: cancelReason,
        turnId: waiter.turnId,
      });
      this.settleForegroundTurnWaiter(waiter);
    }
    agent.foregroundTurnWaiters.clear();
    this.settlePendingForegroundRun(agent.id);
    return {
      ...agent,
      lifecycle: "closed",
      session: null,
      activeForegroundTurnId: null,
    };
  }

  private emitClosedAgent(agent: ManagedAgentClosed, options?: { persist?: boolean }): void {
    this.emitState(agent, options);
  }
  private subscribeToSession(agent: ActiveManagedAgent): void {
    if (agent.unsubscribeSession) {
      return;
    }
    const agentId = agent.id;
    const unsubscribe = agent.session.subscribe((event: AgentStreamEvent) => {
      this.enqueueSessionEvent(agentId, event);
    });
    agent.unsubscribeSession = unsubscribe;
  }

  private enqueueSessionEvent(agentId: string, event: AgentStreamEvent): void {
    const previous = this.sessionEventTails.get(agentId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        const current = this.agents.get(agentId);
        if (!current) {
          return;
        }
        if (current.session == null) {
          return;
        }
        await this.dispatchSessionEvent(current, event);
      })
      .catch((err) => {
        this.logger.error(
          { err, agentId, eventType: event.type },
          "Failed to process session event",
        );
      });

    this.sessionEventTails.set(agentId, next);
    this.trackBackgroundTask(next);
    void next.finally(() => {
      if (this.sessionEventTails.get(agentId) === next) {
        this.sessionEventTails.delete(agentId);
      }
    });
  }

  private async dispatchSessionEvent(
    agent: ActiveManagedAgent,
    event: AgentStreamEvent,
  ): Promise<void> {
    const turnId = (event as { turnId?: string }).turnId;
    const matchingWaiters =
      turnId == null
        ? []
        : Array.from(agent.foregroundTurnWaiters).filter(
            (waiter) => waiter.turnId === turnId && !waiter.settled,
          );

    const shouldNotifyWaiters = await this.handleStreamEvent(agent, event);

    if (!shouldNotifyWaiters) {
      return;
    }

    for (const waiter of matchingWaiters) {
      waiter.callback(event);
      if (isTurnTerminalEvent(event)) {
        this.settleForegroundTurnWaiter(waiter);
      }
    }
  }

  private settleForegroundTurnWaiter(waiter: ForegroundTurnWaiter): void {
    if (waiter.settled) {
      return;
    }
    waiter.settled = true;
    waiter.resolveSettled();
  }

  private createPendingForegroundRun(): PendingForegroundRun {
    let resolveSettled!: () => void;
    const settledPromise = new Promise<void>((resolve) => {
      resolveSettled = resolve;
    });
    return {
      token: randomUUID(),
      started: false,
      settled: false,
      settledPromise,
      resolveSettled,
    };
  }

  private getPendingForegroundRun(agentId: string): PendingForegroundRun | null {
    return this.pendingForegroundRuns.get(agentId) ?? null;
  }

  private hasPendingForegroundRun(agentId: string): boolean {
    return this.pendingForegroundRuns.has(agentId);
  }

  private settlePendingForegroundRun(agentId: string, token?: string): void {
    const pendingRun = this.pendingForegroundRuns.get(agentId);
    if (!pendingRun) {
      return;
    }
    if (token && pendingRun.token !== token) {
      return;
    }

    this.pendingForegroundRuns.delete(agentId);
    if (pendingRun.settled) {
      return;
    }
    pendingRun.settled = true;
    pendingRun.resolveSettled();
  }

  private async resolveInitialPersistedTitle(
    agentId: string,
    config: AgentSessionConfig,
  ): Promise<string | null> {
    const existing = await this.registry?.get(agentId);
    if (existing) {
      return existing.title ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(config, "title")) {
      return config.title ?? null;
    }
    return null;
  }

  private async persistSnapshot(
    agent: ManagedAgent,
    options?: {
      workspaceId?: string;
      title?: string | null;
      internal?: boolean;
      stackId?: string | null;
    },
  ): Promise<void> {
    if (!this.registry) {
      return;
    }
    const hasInternalOverride =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "internal");
    // Internal agents stay ephemeral by default, but some system-owned flows
    // such as quest cards need an exact-id snapshot so their tabs can be
    // reopened after the runner closes the live session.
    if (agent.internal && !hasInternalOverride) {
      return;
    }
    if (options?.workspaceId !== undefined) {
      await this.registry.applySnapshot(agent, options.workspaceId, options);
      return;
    }
    await this.registry.applySnapshot(agent, options);
  }

  private requireRegistry(): AgentStorage {
    if (!this.registry) {
      throw new Error("Agent storage unavailable");
    }
    return this.registry;
  }

  private async refreshSessionState(agent: ActiveManagedAgent): Promise<void> {
    try {
      const modes = await agent.session.getAvailableModes();
      agent.availableModes = modes;
    } catch {
      agent.availableModes = [];
    }

    try {
      agent.currentModeId = await agent.session.getCurrentMode();
    } catch {
      agent.currentModeId = null;
    }

    try {
      const pending = agent.session.getPendingPermissions();
      agent.pendingPermissions = new Map(pending.map((request) => [request.id, request]));
    } catch {
      agent.pendingPermissions.clear();
    }

    this.syncFeaturesFromSession(agent);
    await this.refreshRuntimeInfo(agent);
  }

  private async refreshRuntimeInfo(agent: ActiveManagedAgent): Promise<void> {
    try {
      const newInfo = await agent.session.getRuntimeInfo();
      const changed =
        newInfo.model !== agent.runtimeInfo?.model ||
        newInfo.thinkingOptionId !== agent.runtimeInfo?.thinkingOptionId ||
        newInfo.sessionId !== agent.runtimeInfo?.sessionId ||
        newInfo.modeId !== agent.runtimeInfo?.modeId;
      agent.runtimeInfo = newInfo;
      if (!agent.persistence && newInfo.sessionId) {
        agent.persistence = attachPersistenceCwd(
          { provider: agent.provider, sessionId: newInfo.sessionId },
          agent.cwd,
        );
      }
      // Emit state if runtimeInfo changed so clients get the updated model
      if (changed) {
        this.emitState(agent);
      }
    } catch {
      // Keep existing runtimeInfo if refresh fails.
    }
  }

  private async hydrateTimelineFromLegacyProviderHistory(agent: ActiveManagedAgent): Promise<void> {
    if (agent.historyPrimed) {
      return;
    }
    agent.historyPrimed = true;
    const canonicalUserMessagesById = this.timelineStore.getCanonicalUserMessagesById(agent.id);
    try {
      for await (const event of agent.session.streamHistory()) {
        if (event.type !== "timeline") {
          continue;
        }

        if (event.item.type === "user_message") {
          const eventMessageId = normalizeMessageId(event.item.messageId);
          if (eventMessageId) {
            const canonicalText = canonicalUserMessagesById.get(eventMessageId);
            if (canonicalText === event.item.text) {
              continue;
            }
            // Optimistic-id row already present — stamp the Claude UUID onto
            // it instead of appending a duplicate. See dispatcher comment.
            // Claude-only: other providers may legitimately emit distinct ids
            // for distinct turns that share text.
            if (agent.provider === "claude") {
              const upgradedMessageId = this.timelineStore.upgradeUserMessageWithClaudeUuid(
                agent.id,
                { text: event.item.text, claudeUuid: eventMessageId },
              );
              if (upgradedMessageId !== undefined) {
                continue;
              }
            }
          }
        }

        this.recordTimeline(agent.id, event.item);
      }
    } catch {
      // ignore history failures
    }
  }

  private notifyForegroundTurnWaiters(agentId: string, event: AgentStreamEvent): void {
    const turnId = (event as { turnId?: string }).turnId;
    if (turnId == null) {
      return;
    }

    const agent = this.agents.get(agentId);
    if (!agent) {
      return;
    }

    for (const waiter of agent.foregroundTurnWaiters) {
      if (waiter.turnId === turnId && !waiter.settled) {
        waiter.callback(event);
      }
    }
  }

  private async handleStreamEvent(
    agent: ActiveManagedAgent,
    event: AgentStreamEvent,
    options?: {
      fromHistory?: boolean;
      canonicalUserMessagesById?: ReadonlyMap<string, string>;
    },
  ): Promise<boolean> {
    const eventTurnId = (event as { turnId?: string }).turnId;
    const isForegroundEvent = Boolean(eventTurnId && agent.activeForegroundTurnId === eventTurnId);

    // Only update timestamp for live events, not history replay
    if (!options?.fromHistory) {
      this.touchUpdatedAt(agent);
      if (this.agentStreamCoalescer.handle(agent.id, event)) {
        return false;
      }
      this.agentStreamCoalescer.flushFor(agent.id);
    }

    let shouldDispatchEvent = true;
    let shouldNotifyWaiters = true;

    switch (event.type) {
      case "thread_started":
        {
          const previousSessionId = agent.persistence?.sessionId ?? null;
          const handle = agent.session.describePersistence();
          if (handle) {
            agent.persistence = attachPersistenceCwd(handle, agent.cwd);
            if (agent.persistence?.sessionId !== previousSessionId) {
              this.emitState(agent);
            }
          }
          void this.refreshRuntimeInfo(agent);
        }
        break;
      case "usage_updated":
        agent.lastUsage = { ...agent.lastUsage, ...event.usage };
        this.emitState(agent);
        break;
      case "timeline":
        {
          // Skip provider-replayed user_message items during history hydration.
          if (options?.fromHistory && event.item.type === "user_message") {
            const eventMessageId = normalizeMessageId(event.item.messageId);
            if (eventMessageId) {
              const canonicalText = options?.canonicalUserMessagesById?.get(eventMessageId);
              if (canonicalText === event.item.text) {
                shouldDispatchEvent = false;
                shouldNotifyWaiters = false;
                break;
              }
              // The id-keyed dedup misses when the existing row was minted
              // via `recordUserMessage` (optimistic `msg_…` id) but the SDK
              // echo arrives with the Claude transcript UUID. Upgrade the row
              // in place with the Claude UUID and suppress the echo — same
              // fix as the foreground path below, just applied during history
              // replay. Claude-only: other providers may legitimately emit
              // distinct ids for distinct turns that share text.
              if (agent.provider === "claude") {
                const upgradedMessageId = this.timelineStore.upgradeUserMessageWithClaudeUuid(
                  agent.id,
                  { text: event.item.text, claudeUuid: eventMessageId },
                );
                if (upgradedMessageId !== undefined) {
                  shouldDispatchEvent = false;
                  shouldNotifyWaiters = false;
                  break;
                }
              }
            }
          }

          // Suppress user_message echoes for the active foreground turn.
          if (!options?.fromHistory && event.item.type === "user_message" && isForegroundEvent) {
            const eventMessageId = normalizeMessageId(event.item.messageId);
            if (
              eventMessageId &&
              (await this.hasCommittedUserMessageFromStores(agent.id, {
                messageId: eventMessageId,
                text: event.item.text,
              }))
            ) {
              break;
            }
            // Same-turn echo from the Claude SDK: the optimistic row recorded
            // via `recordUserMessage` carries `messageId = msg_<ts>_<rand>`,
            // but the SDK echo arrives with `messageId = <claude-uuid>`. The
            // id-keyed dedup above can never match in that case. Instead,
            // upgrade the surviving optimistic row by stamping its `claudeUuid`
            // and suppress the echo so we don't append a duplicate. The
            // upgrade lets fork/rewind resolve directly against the Claude
            // transcript instead of falling through to the fragile index path.
            // Claude-only: other providers may legitimately emit distinct ids
            // for distinct turns that share text.
            if (eventMessageId && agent.provider === "claude") {
              const upgradedMessageId = this.timelineStore.upgradeUserMessageWithClaudeUuid(
                agent.id,
                { text: event.item.text, claudeUuid: eventMessageId },
              );
              if (upgradedMessageId !== undefined) {
                break;
              }
            }
          }

          if (options?.fromHistory) {
            this.recordTimeline(agent.id, event.item);
            shouldDispatchEvent = false;
            shouldNotifyWaiters = false;
            break;
          }

          this.recordAndDispatchTimelineItem(agent.id, event.item, event.provider, event.turnId);
          if (event.item.type === "user_message") {
            agent.lastUserMessageAt = new Date();
            this.emitState(agent);
          }
          shouldDispatchEvent = false;
          shouldNotifyWaiters = true;
        }
        break;
      case "turn_completed":
        this.logger.trace(
          {
            agentId: agent.id,
            lifecycle: agent.lifecycle,
            activeForegroundTurnId: agent.activeForegroundTurnId,
            eventTurnId,
          },
          "handleStreamEvent: turn_completed",
        );
        agent.lastUsage = { ...agent.lastUsage, ...event.usage };
        agent.lastError = undefined;
        agent.lastTurnOutcome = "completed";
        // Mirrors `lastUserMessageAt` on the assistant side. Stamped when
        // a turn finishes successfully so the auth-server session
        // projection (and any UI ranking "most-recent activity") sees
        // the LLM's contribution to the conversation — important for
        // long-running autonomous loops where the user hasn't typed in
        // a while but the agent keeps producing turns.
        agent.lastAssistantMessageAt = new Date();
        // For autonomous turns (not foreground), transition to idle
        // unless a replacement is pending (avoid idle flash during replace)
        if (!isForegroundEvent && agent.lifecycle !== "idle" && !agent.pendingReplacement) {
          (agent as ActiveManagedAgent).lifecycle = "idle";
          this.emitState(agent);
        }
        void this.refreshRuntimeInfo(agent);
        break;
      case "turn_failed":
        this.logger.warn(
          {
            agentId: agent.id,
            lifecycle: agent.lifecycle,
            activeForegroundTurnId: agent.activeForegroundTurnId,
            eventTurnId,
            error: event.error,
            code: event.code,
            diagnostic: event.diagnostic,
          },
          "handleStreamEvent: turn_failed",
        );
        // For autonomous turns, set error state directly
        if (!isForegroundEvent) {
          agent.lifecycle = "error";
        }
        agent.lastError = event.error;
        agent.lastTurnOutcome = "failed";
        await this.appendSystemErrorTimelineMessage(
          agent,
          event.provider,
          this.formatTurnFailedMessage(event),
          options,
        );
        for (const [requestId] of agent.pendingPermissions) {
          agent.pendingPermissions.delete(requestId);
          if (!options?.fromHistory) {
            this.dispatchStream(agent.id, {
              type: "permission_resolved",
              provider: event.provider,
              requestId,
              resolution: { behavior: "deny", message: "Turn failed" },
            });
          }
        }
        if (!isForegroundEvent) {
          this.emitState(agent);
        }
        break;
      case "turn_canceled":
        this.logger.trace(
          {
            agentId: agent.id,
            lifecycle: agent.lifecycle,
            activeForegroundTurnId: agent.activeForegroundTurnId,
            eventTurnId,
          },
          "handleStreamEvent: turn_canceled",
        );
        // For autonomous turns, transition to idle
        // unless a replacement is pending (avoid idle flash during replace)
        if (!isForegroundEvent && !agent.pendingReplacement) {
          (agent as ActiveManagedAgent).lifecycle = "idle";
        }
        agent.lastError = undefined;
        agent.lastTurnOutcome = "canceled";
        for (const [requestId] of agent.pendingPermissions) {
          agent.pendingPermissions.delete(requestId);
          if (!options?.fromHistory) {
            this.dispatchStream(agent.id, {
              type: "permission_resolved",
              provider: event.provider,
              requestId,
              resolution: { behavior: "deny", message: "Interrupted" },
            });
          }
        }
        if (!isForegroundEvent) {
          this.emitState(agent);
        }
        break;
      case "turn_started":
        this.logger.trace(
          {
            agentId: agent.id,
            lifecycle: agent.lifecycle,
            activeForegroundTurnId: agent.activeForegroundTurnId,
            eventTurnId,
          },
          "handleStreamEvent: turn_started",
        );
        // For autonomous turn_started (no foreground match), set running
        if (!isForegroundEvent) {
          (agent as ActiveManagedAgent).lifecycle = "running";
          this.emitState(agent);
        }
        break;
      case "permission_requested":
        {
          const hadPendingPermissions = agent.pendingPermissions.size > 0;
          agent.pendingPermissions.set(event.request.id, event.request);
          if (!hadPendingPermissions && !agent.internal) {
            this.broadcastAgentAttention(agent, "permission");
          }
        }
        this.emitState(agent);
        break;
      case "permission_resolved":
        agent.pendingPermissions.delete(event.requestId);
        if (!options?.fromHistory && agent.inFlightPermissionResponses.has(event.requestId)) {
          agent.bufferedPermissionResolutions.set(event.requestId, event);
          shouldDispatchEvent = false;
          break;
        }
        this.emitState(agent);
        break;
      default:
        break;
    }

    if (!options?.fromHistory && isForegroundEvent && isTurnTerminalEvent(event)) {
      this.finalizeForegroundTurn(agent);
    }

    // Skip dispatching individual stream events during history replay.
    if (!options?.fromHistory && shouldDispatchEvent) {
      this.dispatchStream(agent.id, event);
    }

    return shouldNotifyWaiters;
  }

  private recordAndDispatchTimelineItem(
    agentId: string,
    item: AgentTimelineItem,
    provider: AgentProvider,
    turnId?: string,
  ): AgentStreamEvent {
    const row = this.recordTimeline(agentId, item);
    const event: AgentStreamEvent = {
      type: "timeline",
      item,
      provider,
      ...(turnId !== undefined ? { turnId } : {}),
    };
    this.dispatchStream(agentId, event, {
      seq: row.seq,
      epoch: this.timelineStore.getEpoch(agentId),
    });
    return event;
  }

  private async appendSystemErrorTimelineMessage(
    agent: ActiveManagedAgent,
    provider: AgentProvider,
    message: string,
    options?: {
      fromHistory?: boolean;
      canonicalUserMessagesById?: ReadonlyMap<string, string>;
    },
  ): Promise<void> {
    if (options?.fromHistory) {
      return;
    }

    const normalized = message.trim();
    if (!normalized) {
      return;
    }

    const text = `${SYSTEM_ERROR_PREFIX} ${normalized}`;
    const lastItem = await this.getLastItemFromStores(agent.id);
    if (lastItem?.type === "assistant_message" && lastItem.text === text) {
      return;
    }

    const item: AgentTimelineItem = { type: "assistant_message", text };
    const row = this.recordTimeline(agent.id, item);
    this.dispatchStream(
      agent.id,
      {
        type: "timeline",
        item,
        provider,
      },
      {
        seq: row.seq,
        epoch: this.timelineStore.getEpoch(agent.id),
      },
    );
  }

  private formatTurnFailedMessage(
    event: Extract<AgentStreamEvent, { type: "turn_failed" }>,
  ): string {
    const base = event.error.trim();
    const parts = [base.length > 0 ? base : "Provider run failed"];
    const code = event.code?.trim();
    if (code) {
      parts.push(`code: ${code}`);
    }
    const diagnostic = event.diagnostic?.trim();
    if (diagnostic && diagnostic !== base) {
      parts.push(diagnostic);
    }
    return parts.join("\n\n");
  }

  private recordTimeline(agentId: string, item: AgentTimelineItem): AgentTimelineRow {
    const row = this.timelineStore.append(agentId, item);
    this.enqueueDurableTimelineAppend(agentId, row);
    return row;
  }

  private emitState(agent: ManagedAgent, options?: { persist?: boolean }): void {
    // Keep attention as an edge-triggered unread signal, not a level signal.
    this.checkAndSetAttention(agent);
    if (options?.persist !== false) {
      this.enqueueBackgroundPersist(agent);
    }

    this.syncFeaturesFromSession(agent);

    this.dispatch({
      type: "agent_state",
      agent: { ...agent },
    });

    // Fan the state out to the auth-server's session projection.
    // emitState is the universal hook point: every meaningful agent
    // event (create, status flip, message turn, attention change,
    // archive, label/role change) flows through here, so a single
    // call covers them all. Debounced — see markSessionDirty.
    this.markSessionDirty(agent.id);
  }

  /**
   * Schedule a debounced session-projection upsert for this agent. The
   * actual HTTP call fires ~750ms after the *last* call here, coalescing
   * any rapid edits (e.g. a streaming turn fires emitState dozens of
   * times per second; we only want one upsert at the end).
   *
   * Safe to call at any frequency. No-op when no session client is
   * configured (daemon not linked to an auth-server).
   */
  private markSessionDirty(agentId: string): void {
    if (!this.sessionClient) return;
    const existing = this.sessionFanOutTimers.get(agentId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.sessionFanOutTimers.delete(agentId);
      void this.drainSessionFanOut(agentId);
    }, 750);
    this.sessionFanOutTimers.set(agentId, timer);
  }

  /**
   * Build the auth-server upsert payload from the current ManagedAgent
   * and fire it. Errors are caught and logged — the auth-server is best-
   * effort; an outage there must not stall agent work or surface to the
   * user. Skips silently when the agent is no longer in the map (it was
   * closed between the dirty-mark and the drain).
   */
  private async drainSessionFanOut(agentId: string, agentOverride?: ManagedAgent): Promise<void> {
    if (!this.sessionClient) return;
    // Ordering guard: never run two runtime patches for the same agent
    // concurrently — out-of-order HTTP responses could land stale state
    // after fresh state. Defer and re-run once the in-flight one settles.
    if (this.sessionFanOutInFlight.has(agentId)) {
      this.sessionFanOutRerun.set(agentId, agentOverride);
      return;
    }
    const agent = agentOverride ?? this.agents.get(agentId);
    if (!agent) {
      this.sessionFanOutRetryState.delete(agentId);
      return;
    }
    // ownerUserId is required by the auth-server (it validates the user is
    // a member of the daemon's host org). Pre-multi-tenant / legacy agents
    // can have null here — skip the fan-out rather than send an upsert
    // that will be rejected with 403.
    if (!agent.ownerUserId) return;

    // Resolve the canonical project key (git remote URL or `appostleproj-<hex>`
    // marker) — the key the auth-server `projects.project_id` column is
    // populated with by the tab backfill. NOT `ws.projectId` (the workspace
    // registry's id, e.g. `remote:gitlab.com/...`) — keying the runtime patch
    // by that 404s on the auth-server, silently dropping every
    // status/attention/title update. Mirror the backfill exactly. Skip
    // silently when no registry is wired (awaiting_link mode, tests).
    let daemonProjectId: string | null = null;
    if (this.workspaceRegistry) {
      try {
        const workspaces = await this.workspaceRegistry.list();
        const ws = workspaces.find((w) => w.cwd === agent.cwd) ?? null;
        if (ws) {
          daemonProjectId = await resolveCanonicalProjectId(ws.cwd);
        }
      } catch (err) {
        this.logger.debug(
          { err, agentId: agent.id },
          "session fan-out: project key resolve failed",
        );
      }
    }

    // Internal agents don't have tabs — they're spawned by the system and
    // hidden until promoted. The promote path flips `internal` and the next
    // emitState lands here with a non-internal agent and an existing tab.
    if (!daemonProjectId || agent.internal) return;

    const attentionState: "none" | "needs-input" | "permission-prompt" | "error" = agent.attention
      .requiresAttention
      ? agent.attention.attentionReason === "permission"
        ? "permission-prompt"
        : agent.attention.attentionReason === "error"
          ? "error"
          : "needs-input"
      : "none";

    const lastActivityAt = (() => {
      const u = agent.lastUserMessageAt?.getTime() ?? 0;
      const a = agent.lastAssistantMessageAt?.getTime() ?? 0;
      const max = Math.max(u, a);
      return max > 0 ? new Date(max).toISOString() : null;
    })();

    this.sessionFanOutInFlight.add(agentId);
    let result: "ok" | "not_found" | "retryable";
    try {
      result = await this.sessionClient.patchTabRuntime({
        userId: agent.ownerUserId,
        projectId: daemonProjectId,
        targetKind: "agent",
        targetId: agent.id,
        // Mirror the agent title onto the tab so auto-generated names (the
        // hidden haiku titler on materialization) propagate to Recents — the
        // backfill only stamps the title once at boot, and UI renames write it
        // client-side, so without this a daemon-side rename never lands.
        label: agent.title ?? null,
        status: agent.lifecycle,
        // Persist the provider on the tab row so archived tabs render their
        // provider icon without a live daemon. Also self-heals legacy tabs
        // created before the provider column existed.
        provider: agent.provider,
        attentionState,
        pendingPermissionCount: agent.pendingPermissions.size,
        lastActivityAt,
      });
    } finally {
      this.sessionFanOutInFlight.delete(agentId);
    }

    // A fresher drain was requested while this one was in flight — run it
    // now so the auth-server converges on the latest state. Success below
    // still resets the backoff first.
    if (this.sessionFanOutRerun.has(agentId)) {
      const rerunOverride = this.sessionFanOutRerun.get(agentId);
      this.sessionFanOutRerun.delete(agentId);
      if (result === "ok") this.sessionFanOutRetryState.delete(agentId);
      void this.drainSessionFanOut(agentId, rerunOverride ?? agentOverride);
      return;
    }

    if (result === "ok") {
      this.sessionFanOutRetryState.delete(agentId);
      return;
    }
    this.scheduleSessionFanOutRetry(agentId, result, agentOverride);
  }

  private static readonly SESSION_FAN_OUT_BACKOFF_MS = [2_000, 5_000, 15_000, 60_000] as const;
  private static readonly SESSION_FAN_OUT_MAX_NOT_FOUND_RETRIES = 2;

  /**
   * Reschedule the fan-out after a failed runtime patch. Retryable failures
   * (network / 5xx) back off 2s → 5s → 15s → 60s (capped) until a patch
   * succeeds, which resets the ladder. `tab_not_found` gets at most
   * {@link AgentManager.SESSION_FAN_OUT_MAX_NOT_FOUND_RETRIES} retries, then
   * gives up quietly — internal agents legitimately never have a tab row.
   *
   * The retry timer shares `sessionFanOutTimers` with the 750ms debounce, so
   * a newer `markSessionDirty` simply replaces the pending retry — the next
   * drain always reads the freshest agent state (latest state wins).
   */
  private scheduleSessionFanOutRetry(
    agentId: string,
    reason: "not_found" | "retryable",
    agentOverride?: ManagedAgent,
  ): void {
    const state = this.sessionFanOutRetryState.get(agentId) ?? { attempt: 0, notFoundCount: 0 };
    if (reason === "not_found") {
      state.notFoundCount += 1;
      if (state.notFoundCount > AgentManager.SESSION_FAN_OUT_MAX_NOT_FOUND_RETRIES) {
        // Tab genuinely doesn't exist — stop chasing it. A future
        // markSessionDirty (fresh activity) starts over.
        this.sessionFanOutRetryState.delete(agentId);
        return;
      }
    }
    const ladder = AgentManager.SESSION_FAN_OUT_BACKOFF_MS;
    const delay = ladder[Math.min(state.attempt, ladder.length - 1)] ?? 60_000;
    state.attempt += 1;
    this.sessionFanOutRetryState.set(agentId, state);

    const existing = this.sessionFanOutTimers.get(agentId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.sessionFanOutTimers.delete(agentId);
      void this.drainSessionFanOut(agentId, agentOverride).catch((err) => {
        this.logger.debug({ err, agentId }, "session fan-out retry failed");
      });
    }, delay);
    this.sessionFanOutTimers.set(agentId, timer);
  }

  private syncFeaturesFromSession(agent: ManagedAgent): void {
    if ("session" in agent && agent.session?.features) {
      agent.features = agent.session.features;
    }
  }

  private checkAndSetAttention(agent: ManagedAgent): void {
    const previousStatus = this.previousStatuses.get(agent.id);
    const currentStatus = agent.lifecycle;

    // Skip attention tracking for internal agents. Deliberately do NOT
    // advance previousStatuses while internal — otherwise the running→idle
    // edge gets consumed invisibly and a promoted agent can never fire it.
    if (agent.internal) {
      return;
    }

    // Track the new status
    this.previousStatuses.set(agent.id, currentStatus);

    // An error transition always wins: even when attention is already raised
    // (e.g. an unread "finished"), upgrade the reason to "error" so the UI
    // reflects the more severe state. Refresh the timestamp — the error is
    // the newest unread event.
    if (previousStatus !== "error" && currentStatus === "error") {
      const alreadyError =
        agent.attention.requiresAttention && agent.attention.attentionReason === "error";
      if (!alreadyError) {
        agent.attention = {
          requiresAttention: true,
          attentionReason: "error",
          attentionTimestamp: new Date(),
        };
        this.broadcastAgentAttention(agent, "error");
      }
      return;
    }

    // Skip if already requires attention
    if (agent.attention.requiresAttention) {
      return;
    }

    // Check if agent transitioned from running to idle (finished). A
    // user-initiated interrupt also lands running→idle (via turn_canceled),
    // but the user caused it and is looking at it — no attention needed.
    if (previousStatus === "running" && currentStatus === "idle") {
      if (agent.lastTurnOutcome === "canceled") {
        return;
      }
      agent.attention = {
        requiresAttention: true,
        attentionReason: "finished",
        attentionTimestamp: new Date(),
      };
      this.broadcastAgentAttention(agent, "finished");
      return;
    }
  }

  private enqueueBackgroundPersist(agent: ManagedAgent): void {
    const task = this.persistSnapshot(agent).catch((err) => {
      this.logger.error({ err, agentId: agent.id }, "Failed to persist agent snapshot");
    });
    this.trackBackgroundTask(task);
  }

  private enqueueDurableTimelineAppend(agentId: string, row: AgentTimelineRow): void {
    if (!this.durableTimelineStore) {
      return;
    }
    const task = this.durableTimelineStore
      .bulkInsert(agentId, [row])
      .then(() => undefined)
      .catch((err) => {
        this.logger.error(
          { err, agentId, seq: row.seq, itemType: row.item.type },
          "Failed to append timeline row to durable store",
        );
      });
    this.trackBackgroundTask(task);
  }

  private enqueueDurableTimelineBulkInsert(
    agentId: string,
    rows: readonly AgentTimelineRow[],
  ): void {
    if (!this.durableTimelineStore || rows.length === 0) {
      return;
    }
    const task = this.durableTimelineStore.bulkInsert(agentId, rows).catch((err) => {
      this.logger.error(
        { err, agentId, rowCount: rows.length },
        "Failed to seed durable timeline store",
      );
    });
    this.trackBackgroundTask(task);
  }

  private trackBackgroundTask(task: Promise<void>): void {
    this.backgroundTasks.add(task);
    void task.finally(() => {
      this.backgroundTasks.delete(task);
    });
  }

  /**
   * Flush any background persistence work (best-effort).
   * Used by daemon shutdown paths to avoid unhandled rejections after cleanup.
   */
  async flush(): Promise<void> {
    this.agentStreamCoalescer.flushAll();
    // Drain tasks, including tasks spawned while awaiting.
    while (this.backgroundTasks.size > 0) {
      const pending = Array.from(this.backgroundTasks);
      await Promise.allSettled(pending);
    }
  }

  private broadcastAgentAttention(
    agent: ManagedAgent,
    reason: "finished" | "error" | "permission",
  ): void {
    this.onAgentAttention?.({
      agentId: agent.id,
      provider: agent.provider,
      reason,
    });
  }

  private dispatchStream(
    agentId: string,
    event: AgentStreamEvent,
    metadata?: { seq?: number; epoch?: string },
  ): void {
    this.dispatch({ type: "agent_stream", agentId, event, ...metadata });
  }

  private dispatch(event: AgentManagerEvent): void {
    for (const subscriber of this.subscribers) {
      if (
        subscriber.agentId &&
        event.type === "agent_stream" &&
        subscriber.agentId !== event.agentId
      ) {
        continue;
      }
      if (
        subscriber.agentId &&
        event.type === "agent_state" &&
        subscriber.agentId !== event.agent.id
      ) {
        continue;
      }
      // Internal is a UI visibility hint, not a routing gate. Subscribers
      // receive events for ALL agents — including internal ones — and the
      // display layer (workspace tab list, agent list) filters by `internal`
      // at consumption time. This way, the UI naturally surfaces a session
      // the moment its `internal` flag flips to false (i.e. on promote);
      // there is no separate hand-off to perform because the data has been
      // flowing the whole time.
      subscriber.callback(event);
    }
  }

  private async normalizeConfig(config: AgentSessionConfig): Promise<AgentSessionConfig> {
    const normalized: AgentSessionConfig = { ...config };

    // Always resolve cwd to absolute path for consistent history file lookup
    if (normalized.cwd) {
      normalized.cwd = resolve(normalized.cwd);
      try {
        const cwdStats = await stat(normalized.cwd);
        if (!cwdStats.isDirectory()) {
          throw new Error(`Working directory is not a directory: ${normalized.cwd}`);
        }
      } catch (error) {
        if (
          error instanceof Error &&
          "code" in error &&
          (error as NodeJS.ErrnoException).code === "ENOENT"
        ) {
          throw new Error(`Working directory does not exist: ${normalized.cwd}`);
        }
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(`Failed to access working directory: ${normalized.cwd}`);
      }
    }

    if (typeof normalized.model === "string") {
      const trimmed = normalized.model.trim();
      normalized.model = trimmed.length > 0 && trimmed !== "default" ? trimmed : undefined;
    }

    if (!normalized.model) {
      const client = this.clients.get(normalized.provider);
      if (client) {
        try {
          const models = await client.listModels();
          const defaultModel = models.find((model) => model.isDefault) ?? models[0];
          if (defaultModel) {
            normalized.model = defaultModel.id;
          }
        } catch {
          // Provider may not support model listing — leave model undefined
        }
      }
    }

    if (!normalized.modeId) {
      try {
        normalized.modeId =
          getAgentProviderDefinition(normalized.provider).defaultModeId ?? undefined;
      } catch {
        // Unknown provider
      }
    }

    return normalized;
  }

  private buildLaunchContext(
    agentId: string,
    opts?: { connectingUserId?: string | null; provider?: string },
  ): AgentLaunchContext {
    const env: Record<string, string> = {
      APPOSTLE_AGENT_ID: agentId,
    };

    const connectingUserId = opts?.connectingUserId ?? null;
    // A "guest" is any verified connecting user that is not the daemon owner.
    // When both ids are unknown (isolated unit tests) this is false → owner path.
    const isGuest =
      connectingUserId !== null &&
      this.ownerUserId !== null &&
      connectingUserId !== this.ownerUserId;

    // Per-user Claude account routing applies ONLY to Claude Code agents. Guests
    // running other providers (codex, opencode, …) carry their own auth through
    // those providers and must not be gated on a missing Claude token.
    if (isGuest && opts?.provider === "claude") {
      const stored = this.claudeTokenStore?.get(connectingUserId) ?? null;
      if (stored) {
        // Run under the guest's own Claude account (token) with an isolated config
        // dir so their history/state never mixes with the owner's.
        env.CLAUDE_CODE_OAUTH_TOKEN = stored.token;
        env.CLAUDE_CONFIG_DIR = ensureClaudeProfile(connectingUserId, this.logger);
        this.logger.info(
          { userId: connectingUserId, profileDir: env.CLAUDE_CONFIG_DIR },
          "using guest claude account token",
        );
      } else if (this.requireGuestClaudeToken) {
        // Fail loud instead of silently spending the owner's Claude subscription.
        throw new ClaudeAccountRequiredError(connectingUserId);
      } else {
        // Guard parked (requireGuestClaudeToken=false): the bring-your-own-account
        // feature is fully built but enforcement is off pre-launch, so a tokenless
        // guest falls back to the owner's Claude account (pre-guard behavior).
        // Re-enable enforcement before launch. See ClaudeAccountRequiredError.
        this.logger.info(
          { userId: connectingUserId },
          "guest claude token guard disabled; running under owner account",
        );
      }
    }

    return { env, chromeEnabled: this.chromeEnabled, playwrightEnabled: this.playwrightEnabled };
  }

  private requireClient(provider: AgentProvider): AgentClient {
    const client = this.clients.get(provider);
    if (!client) {
      throw new Error(`No client registered for provider '${provider}'`);
    }
    return client;
  }

  private requireAgent(id: string): LiveManagedAgent {
    const normalizedId = validateAgentId(id, "requireAgent");
    const agent = this.agents.get(normalizedId);
    if (!agent) {
      throw new Error(`Unknown agent '${normalizedId}'`);
    }
    return agent;
  }

  private requireSessionAgent(id: string): ActiveManagedAgent {
    const agent = this.requireAgent(id);
    if (agent.session === null) {
      throw new Error(`Agent '${agent.id}' has no managed session`);
    }
    return agent;
  }
}
